import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import CampaignDetails from './CampaignDetails';
import { apiService } from '../services/apiService';
import { useSocket } from '../hooks/useSocket';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Default configuration objects - will be populated from API
const DEFAULT_AI_CONFIG = {
  stt_model: '',
  llm_model: '',
  tts_model: '',
  voice: '',
  tts_speed: 1.0,
  language: '' // Explicit language field
};

const DEFAULT_SIP_CONFIG = {
  provider: 'flashcall',
  caller_id: '+33000000000'
};

const DEFAULT_SCHEDULE_CONFIG = {
  enabled: false,
  timezone: 'Europe/Paris',
  days_of_week: [1, 2, 3, 4, 5], // Monday=1 to Friday=5
  time_slots: [
    { start: '09:00', end: '17:00' }
  ]
};

const DEFAULT_AMD_CONFIG = {
  enabled: false,
  initial_silence: 2500,
  greeting: 1500,
  after_greeting_silence: 800,
  total_analysis_time: 5000,
  min_word_length: 100,
  between_words_silence: 50,
  maximum_number_of_words: 2,
  silence_threshold: 256,
  maximum_word_length: 5000
};


const DEFAULT_GOOGLE_SHEET_CONFIG = {
  sheet_url: '',
  sync_enabled: false
};

const DEFAULT_LEAD_QUALIFICATION = {
  min_positive_answers: 4,
  custom_lead_prompt: '',
  require_owner: false,
  require_house: false
};

const DEFAULT_CAMPAIGN_DATA = {
  name: '',
  description: '',
  script: '',
  selected_script_id: null,
  conversation_mode: 'script',
  ai_config: DEFAULT_AI_CONFIG,
  sip_config: DEFAULT_SIP_CONFIG,
  schedule_config: DEFAULT_SCHEDULE_CONFIG,
  background_ambiance: 'none',
  concurrent_calls: 5,
  amd_config: DEFAULT_AMD_CONFIG,
  google_sheet_config: DEFAULT_GOOGLE_SHEET_CONFIG,
  faq_list: [],
  faq_threshold: 0.70,
  lead_qualification: DEFAULT_LEAD_QUALIFICATION
};

// Mirrors backend utils/leadDecision.js defaultOutcomeForCategory — shown as the
// effective value for categories saved before lead_outcome existed.
const LEAD_OUTCOME_OPTIONS = [
  { value: 'neutral', label: 'Neutral — threshold decides' },
  { value: 'lead', label: 'Always a lead' },
  { value: 'not_lead', label: 'Never a lead' }
];
const categoryKey = (id) => String(id || '').split('-')[0].normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
const defaultLeadOutcome = (id) => {
  const key = categoryKey(id);
  if (/^RDV/.test(key)) return 'lead';
  if (/^(FAUX_NUMERO|FAUX_NUM|PAS_INTERESSE|HORS_CRITERE|REPONDEUR)/.test(key)) return 'not_lead';
  return 'neutral';
};

// Eliminatory criteria toggles — shared by the create and edit panels.
const CriteriaToggles = ({ value, onChange }) => (
  <div className="border-t pt-4 space-y-3">
    <span className="block text-sm font-medium">🚫 Eliminatory criteria</span>
    <span className="block text-sm text-gray-500">
      ℹ️ A disqualifying answer ends the call immediately and the call can never be a lead. Leave both off for campaigns without these criteria.
    </span>
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={value?.require_owner || false}
        onChange={(e) => onChange({ require_owner: e.target.checked })}
        className="mt-1 h-4 w-4 accent-blue-600 cursor-pointer"
      />
      <span>
        <span className="block text-sm font-medium">🏠 Owners only (propriétaire) — exclude renters (locataire)</span>
      </span>
    </label>
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={value?.require_house || false}
        onChange={(e) => onChange({ require_house: e.target.checked })}
        className="mt-1 h-4 w-4 accent-blue-600 cursor-pointer"
      />
      <span>
        <span className="block text-sm font-medium">🏡 Houses only (maison) — exclude apartments (appartement)</span>
      </span>
    </label>
  </div>
);

const DEFAULT_NEW_CONTACT = {
  name: '',
  firstName: '',
  phone: '',
  email: ''
};

// Helper functions
const resetCampaignData = () => ({ ...DEFAULT_CAMPAIGN_DATA });
const resetNewContact = () => ({ ...DEFAULT_NEW_CONTACT });

const SAMPLE_CUSTOM_FIELDS = [
  {
    id: 'nom',
    name: 'nom',
    label: 'Nom',
    type: 'text',
    description: 'Nom de famille du client',
    required: false,
    options: []
  },
  {
    id: 'prenom',
    name: 'prenom',
    label: 'Prénom',
    type: 'text',
    description: 'Prénom du client',
    required: false,
    options: []
  },
  {
    id: 'adresse',
    name: 'adresse',
    label: 'Adresse',
    type: 'text',
    description: 'Adresse complète du domicile',
    required: false,
    options: []
  },
  {
    id: 'code_postal',
    name: 'code_postal',
    label: 'Code postal',
    type: 'text',
    description: 'Code postal du domicile',
    required: false,
    options: []
  },
  {
    id: 'ville',
    name: 'ville',
    label: 'Ville',
    type: 'text',
    description: 'Ville de résidence',
    required: false,
    options: []
  },
  {
    id: 'mode_chauffage',
    name: 'mode_chauffage',
    label: 'Mode de chauffage',
    type: 'select',
    description: 'Mode de chauffage principal du logement',
    required: false,
    options: ['Électrique', 'Gaz', 'Fioul', 'Bois', 'Pompe à chaleur', 'Autre']
  },
  {
    id: 'montant_facture_energetique',
    name: 'montant_facture_energetique',
    label: 'Montant de la facture énergétique du foyer',
    type: 'text',
    description: 'Montant mensuel ou annuel approximatif des factures énergétiques',
    required: false,
    options: []
  },
  {
    id: 'marie',
    name: 'marie',
    label: 'Marié(e)',
    type: 'select',
    description: 'Indique si le client est marié',
    required: false,
    options: ['Oui', 'Non']
  },
  {
    id: 'nombre_enfants_charge',
    name: 'nombre_enfants_charge',
    label: 'Nombre d’enfants à charge',
    type: 'number',
    description: 'Nombre d’enfants à charge dans le foyer',
    required: false,
    options: []
  },
  {
    id: 'profession',
    name: 'profession',
    label: 'Profession',
    type: 'text',
    description: 'Profession ou activité principale du client',
    required: false,
    options: []
  },
  {
    id: 'revenus',
    name: 'revenus',
    label: 'Revenus',
    type: 'select',
    description: 'Tranche de revenus du foyer',
    required: false,
    options: ['<20k', '20k-30k', '30k-40k', '40k-60k', '60k+']
  },
  {
    id: 'superficie_maison',
    name: 'superficie_maison',
    label: 'Superficie de la maison',
    type: 'text',
    description: 'Superficie du logement en mètres carrés',
    required: false,
    options: []
  },
  {
    id: 'annee_construction',
    name: 'annee_construction',
    label: 'Année de construction',
    type: 'text',
    description: 'Année approximative de construction du logement',
    required: false,
    options: []
  },
  {
    id: 'date_rappel_souhaitee',
    name: 'date_rappel_souhaitee',
    label: 'Date de rappel souhaitée',
    type: 'date',
    description: 'Date à laquelle le client souhaite être rappelé',
    required: false,
    options: []
  },
  {
    id: 'heure_rappel_souhaitee',
    name: 'heure_rappel_souhaitee',
    label: 'Heure de rappel souhaitée',
    type: 'time',
    description: 'Heure à laquelle le client souhaite être rappelé',
    required: false,
    options: []
  },
  {
    id: 'note_interet_client',
    name: 'note_interet_client',
    label: 'Note de l’intérêt client (1 à 10)',
    type: 'number',
    description: 'Niveau d’intérêt du client évalué de 1 à 10',
    required: false,
    options: []
  },
  {
    id: 'date_de_la_campagne',
    name: 'date_de_la_campagne',
    label: 'Date de la campagne',
    type: 'date',
    description: 'Date de lancement ou d’exécution de la campagne',
    required: false,
    options: []
  },
  {
    id: 'temps_de_campagne',
    name: 'temps_de_campagne',
    label: 'Temps de la campagne',
    type: 'time',
    description: 'Heure ou durée de la campagne',
    required: false,
    options: []
  },
  {
    id: 'contact',
    name: 'contact',
    label: 'Contact',
    type: 'text',
    description: 'Numéro de téléphone du contact',
    required: false,
    options: []
  },
  {
    id: 'proprietaire_ou_locataire',
    name: 'proprietaire_ou_locataire',
    label: 'Propriétaire ou Locataire',
    type: 'select',
    description: 'Propriétaire ou locataire du logement',
    required: false,
    options: ['Propriétaire', 'Locataire']
  },
  {
    id: 'maison_ou_appartement',
    name: 'maison_ou_appartement',
    label: 'Maison ou Appartement',
    type: 'select',
    description: 'Type de logement : maison ou appartement',
    required: false,
    options: ['Maison', 'Appartement']
  }
];

const SAMPLE_CALL_CATEGORIES = [
  {
    id: 'HORS_CRITERE',
    name: 'HORS CRITÈRE',
    description: 'Le prospect ne répond pas aux critères d’éligibilité',
    color: '#a855f7'
  },
  {
    id: 'FAUX_NUMERO',
    name: 'FAUX NUMÉRO',
    description: 'Le numéro de téléphone est incorrect ou invalide',
    color: '#ef4444'
  },
  {
    id: 'PAS_INTERESSE',
    name: 'PAS INTÉRESSÉ',
    description: 'Le prospect n’est pas intéressé par l’offre',
    color: '#facc15'
  },
  {
    id: 'A_RAPPELER',
    name: 'À RAPPELER',
    description: 'Le prospect souhaite être rappelé ultérieurement',
    color: '#3b82f6'
  },
  {
    id: 'RDV_PRIS',
    name: 'RDV PRIS',
    description: 'Un rendez-vous a été fixé avec le prospect',
    color: '#22c55e'
  },
  {
    id: 'REPONDEUR',
    name: 'RÉPONDEUR',
    description: 'Le prospect n’a pas répondu, messagerie vocale atteinte',
    color: '#9ca3af'
  }
];

const cloneSampleItems = (items) =>
  items.map((item) => ({
    ...item,
    id: `${item.id || item.name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
  }));

// Normalize display names to model IDs for backward compatibility
const normalizeModelName = (modelName) => {
  if (!modelName || typeof modelName !== 'string') return modelName;

  // Mapping of display names to model IDs
  const modelMap = {
    // STT Models
    'OpenAI Whisper Tiny EN (English)': 'whisper_tiny_en',
    'OpenAI Whisper Tiny FR (French)': 'whisper_tiny_fr',
    'OpenAI Whisper Tiny': 'whisper_tiny',
    // LLM Models
    'Qwen2-1.5B EN (English)': 'qwen2_15b_en',
    'Qwen2-1.5B FR (French)': 'qwen2_15b_fr',
    'Qwen2-1.5B': 'qwen2_15b',
    'Lucie-7B (French)': 'lucie_7b',
    'Lucie-7B': 'lucie_7b',
    'GPT2 Small': 'gpt2_small',
    // TTS Models
    'Kokoro': 'kokoro',
    'Piper': 'piper',
    'Piper (French)': 'piper',
    'Piper (English)': 'piper',
    'GTTS': 'gtts',
    'French TTS': 'french_tts',
    'Chatterbox': 'chatterbox',
    'Chatterbox (French)': 'chatterbox',
    'Chatterbox (English)': 'chatterbox'
  };

  // Check if it's already a model ID (contains underscores and lowercase)
  if (modelName.includes('_') && modelName === modelName.toLowerCase()) {
    return modelName; // Already a model ID
  }

  // Try to find in map
  if (modelMap[modelName]) {
    return modelMap[modelName];
  }

  // Try partial matching for display names
  const lowerName = modelName.toLowerCase();
  if (lowerName.includes('faster') && lowerName.includes('whisper')) {
    return 'faster_whisper_large_v3';
  }
  if (lowerName.includes('whisper') && lowerName.includes('tiny')) {
    if (lowerName.includes('french') || lowerName.includes('fr')) {
      return 'whisper_tiny_fr';
    } else if (lowerName.includes('english') || lowerName.includes('en')) {
      return 'whisper_tiny_en';
    }
    return 'whisper_tiny';
  }
  if (lowerName.includes('lucie')) {
    return 'lucie_7b';
  }
  if (lowerName.includes('qwen2') || lowerName.includes('qwen')) {
    if (lowerName.includes('french') || lowerName.includes('fr')) {
      return 'qwen2_15b_fr';
    } else if (lowerName.includes('english') || lowerName.includes('en')) {
      return 'qwen2_15b_en';
    }
    return 'qwen2_15b';
  }
  if (lowerName.includes('gpt2') || lowerName.includes('gpt')) {
    return 'gpt2_small';
  }
  if (lowerName.includes('kokoro')) {
    return 'kokoro';
  }
  if (lowerName.includes('piper')) {
    return 'piper';
  }
  if (lowerName.includes('chatterbox') || lowerName.includes('chatter_box')) {
    return 'chatterbox';
  }
  if (lowerName.includes('gtts') || (lowerName.includes('french') && lowerName.includes('tts'))) {
    return 'gtts';
  }

  // Return original if no match found
  return modelName;
};

const normalizeCampaignConfig = (campaign) => {
  if (!campaign) return campaign;
  const normalized = {
    ...campaign,
    customFields: campaign.customFields || campaign.custom_fields || [],
    callCategories: campaign.callCategories || campaign.call_categories || [],
    lead_qualification: { ...DEFAULT_LEAD_QUALIFICATION, ...(campaign.lead_qualification || {}) }
  };

  // Normalize ai_config if it exists (convert display names to model IDs)
  if (normalized.ai_config) {
    normalized.ai_config = {
      ...normalized.ai_config,
      stt_model: normalizeModelName(normalized.ai_config.stt_model),
      llm_model: normalizeModelName(normalized.ai_config.llm_model),
      tts_model: normalizeModelName(normalized.ai_config.tts_model)
    };
  }

  return normalized;
};

// Modal helper functions
const openModal = (setModalState, modalType) => {
  setModalState(prev => ({ ...prev, [modalType]: true }));
};

const closeModal = (setModalState, modalType) => {
  setModalState(prev => ({ ...prev, [modalType]: false }));
};

const SimplifiedCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [scripts, setScripts] = useState([]);

  // Use infinite scroll for campaigns
  const {
    data: campaignsData,
    loading: loadingCampaigns,
    hasMore: hasMoreCampaigns,
    observerTarget: campaignsObserverTarget,
    reset: resetCampaigns
  } = useInfiniteScroll(
    async (page, limit) => {
      const response = await apiService.campaigns.getAll({ page, limit });
      // Handle paginated response format
      if (response.data?.campaigns) {
        return {
          ...response,
          data: {
            ...response.data,
            campaigns: response.data.campaigns.map(normalizeCampaignConfig)
          }
        };
      }
      // Handle legacy array format
      if (Array.isArray(response.data)) {
        return {
          ...response,
          data: {
            campaigns: response.data.map(normalizeCampaignConfig),
            pagination: { hasNextPage: false }
          }
        };
      }
      return response;
    },
    {
      initialPage: 1,
      pageSize: 20,
      enabled: true
    }
  );

  // Sync campaigns state with hook data
  useEffect(() => {
    if (campaignsData && campaignsData.length > 0) {
      setCampaigns(campaignsData);
    }
  }, [campaignsData]);
  const [modalState, setModalState] = useState({
    showCreate: false,
    showEdit: false,
    showDetail: false,
    showEnrichment: false,
    showExportFormat: false,
    showDeleteConfirm: false
  });
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [exportingCampaignId, setExportingCampaignId] = useState(null);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startingCampaignId, setStartingCampaignId] = useState(null);
  const [pausingCampaignId, setPausingCampaignId] = useState(null);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [savingEnrichment, setSavingEnrichment] = useState(false);
  const [clearingCacheId, setClearingCacheId] = useState(null);
  const [regeneratingTtsId, setRegeneratingTtsId] = useState(null);
  const [recycleModal, setRecycleModal] = useState(null); // { campaign, options, cats:[], outs:[], name, creating }
  const [newContact, setNewContact] = useState(DEFAULT_NEW_CONTACT);
  const [pregenProgress, setPregenProgress] = useState({}); // { campaignId: { completed, total, phase } }
  const [customFields, setCustomFields] = useState([]);
  const [callCategories, setCallCategories] = useState([]);
  const [campaignData, setCampaignData] = useState(DEFAULT_CAMPAIGN_DATA);

  // Language selection - must be selected first
  const [selectedLanguage, setSelectedLanguage] = useState('');

  // States for CSV import
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [mappedContacts, setMappedContacts] = useState([]);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [columnMapping, setColumnMapping] = useState({});

  // States for new features
  const [availableAmbiances, setAvailableAmbiances] = useState([]);
  const [currentTime, setCurrentTime] = useState(null);

  // Available models - loaded dynamically via API
  const [availableModels, setAvailableModels] = useState({
    tts: [],
    stt: [],
    llm: [],
    voices: []
  });

  // Available voices for selected TTS
  const [availableVoices, setAvailableVoices] = useState([]);

  // Available SIP providers - loaded from API
  const [availableSipProviders, setAvailableSipProviders] = useState([
    { id: 'flashcall', name: 'Flashcall', status: 'available' } // Temporary fallback
  ]);

  // Initialize Socket.io connection for real-time updates
  const { on, off } = useSocket();

  // Load SIP providers from API
  const loadAvailableSipProviders = async () => {
    try {
      const response = await apiService.sip.getProfiles();
      const providers = response.data.map(profile => ({
        id: profile.name.toLowerCase().replace(/\s+/g, '_'),
        name: profile.name,
        status: profile.enabled ? 'available' : 'disabled',
        enabled: profile.enabled
      }));
      setAvailableSipProviders(providers);
    } catch (error) {
      console.error('Error loading SIP providers:', error);
    }
  };

  // Load available models from API
  const loadAvailableModels = async () => {
    try {
      const response = await apiService.aiModels.getAvailable();
      if (response.data && response.data.models) {
        setAvailableModels(response.data.models);
      }
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  // Load models from API on component mount
  useEffect(() => {
    loadAvailableModels();
  }, []);

  // Filter models by selected language
  const getFilteredModels = (modelType) => {
    if (!selectedLanguage) return [];
    return availableModels[modelType]?.filter(model => {
      // PRIORITY 1: Check if model has explicit language property (most reliable)
      if (model.language) {
        return model.language === selectedLanguage;
      }

      // PRIORITY 2: For STT models, check known multi-language models
      if (modelType === 'stt') {
        const multiLanguageSTT = ['faster_whisper_large_v3', 'whisper_tiny'];
        const modelIdLower = model.id.toLowerCase();
        if (multiLanguageSTT.some(stt => modelIdLower.includes(stt))) {
          // These models support both languages, but should be filtered by language property
          // If no language property, include for both (fallback)
          return true;
        }
      }

      // PRIORITY 3: For LLM models, check known multi-language models
      if (modelType === 'llm') {
        const multiLanguageLLM = ['lucie_7b', 'qwen2_15b', 'calme_3b'];
        const modelIdLower = model.id.toLowerCase();
        if (multiLanguageLLM.some(llm => modelIdLower.includes(llm))) {
          // These models support both languages, but should be filtered by language property
          // If no language property, include for both (fallback)
          return true;
        }
      }

      // PRIORITY 4: For TTS models, check known French TTS models (Piper and Chatterbox support both but primarily French)
      if (modelType === 'tts' && selectedLanguage === 'fr') {
        const frenchTTSModels = ['gtts', 'french_tts', 'piper', 'chatterbox'];
        const modelIdLower = model.id.toLowerCase();
        if (frenchTTSModels.some(tts => modelIdLower.includes(tts))) {
          return true;
        }
      }

      // PRIORITY 5: For TTS models, check known English TTS models (Piper and Chatterbox support both but primarily French)
      if (modelType === 'tts' && selectedLanguage === 'en') {
        const englishTTSModels = ['kokoro', 'piper', 'chatterbox'];
        const modelIdLower = model.id.toLowerCase();
        if (englishTTSModels.some(tts => modelIdLower.includes(tts))) {
          return true;
        }
      }

      // PRIORITY 6: Check model ID for language suffix
      if (selectedLanguage === 'en') {
        return model.id.includes('_en') || (!model.id.includes('_fr') && !model.id.includes('_de') && !model.id.includes('_es'));
      }
      if (selectedLanguage === 'fr') {
        return model.id.includes('_fr');
      }
      return true;
    }) || [];
  };

  // Reset AI config when language changes
  useEffect(() => {
    if (selectedLanguage) {
      setCampaignData(prev => ({
        ...prev,
        ai_config: {
          ...(prev.ai_config || DEFAULT_AI_CONFIG),
          stt_model: '',
          llm_model: '',
          tts_model: '',
          voice: '',
          language: selectedLanguage
        }
      }));
    }
  }, [selectedLanguage]);

  // Load available voices when TTS model changes
  useEffect(() => {
    const loadVoicesForTTS = async () => {
      if (campaignData.ai_config?.tts_model) {
        try {
          const response = await apiService.voices.getByProvider(campaignData.ai_config.tts_model);
          const voices = response.data || [];
          setAvailableVoices(voices);

          // Auto-select first voice if none selected and voices available
          if (voices.length > 0 && !campaignData.ai_config.voice) {
            setCampaignData(prev => ({
              ...prev,
              ai_config: {
                ...(prev.ai_config || DEFAULT_AI_CONFIG),
                voice: voices[0].id
              }
            }));
          } else if (voices.length > 0 && !voices.find(voice => voice.id === campaignData.ai_config?.voice)) {
            // Reset voice if current selection is no longer available
            setCampaignData(prev => ({
              ...prev,
              ai_config: {
                ...(prev.ai_config || DEFAULT_AI_CONFIG),
                voice: voices[0].id
              }
            }));
          } else if (voices.length === 0) {
            // Clear voice selection if no voices available
            setCampaignData(prev => ({
              ...prev,
              ai_config: {
                ...(prev.ai_config || DEFAULT_AI_CONFIG),
                voice: ''
              }
            }));
          }
        } catch (error) {
          console.error('Error loading voices for TTS:', error);
          setAvailableVoices([]);
          setCampaignData(prev => ({
            ...prev,
            ai_config: {
              ...(prev.ai_config || DEFAULT_AI_CONFIG),
              voice: ''
            }
          }));
        }
      } else {
        setAvailableVoices([]);
        setCampaignData(prev => ({
          ...prev,
          ai_config: {
            ...(prev.ai_config || DEFAULT_AI_CONFIG),
            voice: ''
          }
        }));
      }
    };

    loadVoicesForTTS();
  }, [campaignData.ai_config?.tts_model]);

  // Load available ambiances
  const loadAvailableAmbiances = async () => {
    try {
      const response = await apiService.ambiances.getAvailable();
      console.log('✅ Loaded ambiances from API:', response.data);
      if (response.data && response.data.ambiances) {
        setAvailableAmbiances(response.data.ambiances);
        console.log(`✅ Set ${response.data.ambiances.length} ambiances`);
      } else {
        console.warn('⚠️ API response missing ambiances array, using fallback');
        setAvailableAmbiances([
          { id: 'none', name: 'No Ambiance', description: 'No background ambiance' }
        ]);
      }
    } catch (error) {
      console.error('❌ Error loading ambiances from API:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Fallback with default ambiances - but this should not be needed if API works
      setAvailableAmbiances([
        { id: 'none', name: 'No Ambiance', description: 'No background ambiance' }
      ]);
    }
  };

  // Voice test function - play a sample of the selected voice
  const testVoice = async () => {
    try {
      if (!campaignData.ai_config?.voice) {
        toast.error('Please select a voice first');
        return;
      }

      toast.info('🔊 Testing voice in progress...');

      const testText = selectedLanguage === 'en'
        ? 'Hello, this is a test of the selected voice for VoxiPro AI.'
        : 'Bonjour, ceci est un test de la voix sélectionnée pour VoxiPro AI.';

      const response = await apiService.tts.generate({
        text: testText,
        model: campaignData.ai_config.tts_model,
        voice: campaignData.ai_config.voice,
        format: 'wav',
        use_case: 'voice_test'
      });

      if (response.data.success) {
        const audio = new Audio();
        const audioUrl = response.data.audio_url.startsWith('http')
          ? response.data.audio_url
          : `${BACKEND_URL}${response.data.audio_url}`;
        audio.src = audioUrl;
        audio.onloadeddata = () => { toast.success('🎵 Voice test successful!'); audio.play(); };
        audio.onerror = () => { toast.error('Error during audio playback'); };
        audio.onended = () => { toast.info('🎵 Audio playback completed'); };
      } else {
        toast.error(response.data.message || 'Error during voice test');
      }
    } catch (error) {
      console.error('Error testing voice:', error);
      toast.error('Error testing voice: ' + (error.response?.data?.message || error.message));
    }
  };

  useEffect(() => {
    loadData();
    loadAvailableSipProviders();
    loadAvailableAmbiances();
  }, []);

  // Set up real-time Socket.io listeners (merge socket payload into state)
  useEffect(() => {
    const handleCampaignUpdate = (data) => {
      console.log('🔔 Received campaign:updated event:', data);
      if (!data.campaignId) return;

      const cid = data.campaignId.toString();

      setCampaigns(prev => {
        const idx = prev.findIndex(c => (c._id || c._id?.toString()) === cid);
        if (idx >= 0) {
          const next = [...prev];
          const existing = next[idx];
          next[idx] = {
            ...existing,
            status: data.status ?? existing.status,
            execution_state: data.execution_state ?? existing.execution_state,
            calls_made: data.calls_made !== undefined ? data.calls_made : existing.calls_made,
            confirmed_calls: data.confirmed_calls !== undefined ? data.confirmed_calls : existing.confirmed_calls,
            success_rate: data.success_rate !== undefined ? data.success_rate : existing.success_rate,
            analytics_snapshot: data.analytics_snapshot ?? existing.analytics_snapshot,
            recordings_count: data.recordings_count !== undefined ? data.recordings_count : existing.recordings_count
          };
          return next;
        }
        return prev;
      });

      setSelectedCampaign(prev => {
        if (!prev || (prev._id?.toString() !== cid)) return prev;
        return {
          ...prev,
          status: data.status ?? prev.status,
          execution_state: data.execution_state ?? prev.execution_state,
          calls_made: data.calls_made !== undefined ? data.calls_made : prev.calls_made,
          confirmed_calls: data.confirmed_calls !== undefined ? data.confirmed_calls : prev.confirmed_calls,
          success_rate: data.success_rate !== undefined ? data.success_rate : prev.success_rate,
          analytics_snapshot: data.analytics_snapshot ?? prev.analytics_snapshot,
          recordings_count: data.recordings_count !== undefined ? data.recordings_count : prev.recordings_count
        };
      });

      setEditingCampaign(prev => {
        if (!prev || (prev._id?.toString() !== cid)) return prev;
        return {
          ...prev,
          status: data.status ?? prev.status,
          execution_state: data.execution_state ?? prev.execution_state,
          calls_made: data.calls_made !== undefined ? data.calls_made : prev.calls_made,
          confirmed_calls: data.confirmed_calls !== undefined ? data.confirmed_calls : prev.confirmed_calls,
          success_rate: data.success_rate !== undefined ? data.success_rate : prev.success_rate,
          analytics_snapshot: data.analytics_snapshot ?? prev.analytics_snapshot,
          recordings_count: data.recordings_count !== undefined ? data.recordings_count : prev.recordings_count
        };
      });
    };

    const handleCallCreated = (data) => {
      console.log('🔔 Received call:created event:', data);
      if (!data.campaignId) return;
      const cid = data.campaignId.toString();
      setCampaigns(prev => {
        const idx = prev.findIndex(c => (c._id?.toString()) === cid);
        if (idx < 0) return prev;
        const next = [...prev];
        const c = next[idx];
        next[idx] = { ...c, calls_made: (c.calls_made || 0) + 1 };
        return next;
      });
    };

    on('campaign:updated', handleCampaignUpdate);
    on('call:created', handleCallCreated);

    // ✅ Listen for TTS pre-generation status updates
    const handlePregenStatus = (data) => {
      if (!data.campaignId) return;
      const cid = data.campaignId.toString();
      setCampaigns(prev => prev.map(c =>
        (c._id?.toString()) === cid ? { ...c, tts_pregen_status: data.status } : c
      ));
      if (data.status === 'completed') {
        toast.success('🎵 Audio pre-generation completed — campaign ready to start!');
        // Clear progress when completed
        setPregenProgress(prev => { const next = { ...prev }; delete next[cid]; return next; });
      } else if (data.status === 'failed') {
        toast.warning('⚠️ Audio pre-generation failed — campaign can still start (will generate live)');
        setPregenProgress(prev => { const next = { ...prev }; delete next[cid]; return next; });
      }
    };
    on('campaign:pregen:status', handlePregenStatus);

    // ✅ Listen for TTS pre-generation PROGRESS updates (per-line)
    const handlePregenProgress = (data) => {
      if (!data.campaignId) return;
      const cid = data.campaignId.toString();
      setPregenProgress(prev => ({
        ...prev,
        [cid]: { completed: data.completed, total: data.total, phase: data.phase }
      }));
    };
    on('campaign:pregen:progress', handlePregenProgress);

    return () => {
      off('campaign:updated', handleCampaignUpdate);
      off('call:created', handleCallCreated);
      off('campaign:pregen:status', handlePregenStatus);
      off('campaign:pregen:progress', handlePregenProgress);
    };
  }, [on, off]);

  const loadScripts = async () => {
    try {
      const scriptsResponse = await apiService.scripts.getAll();
      // Handle both paginated and legacy formats
      const scriptsData = scriptsResponse.data?.scripts || scriptsResponse.data || [];
      setScripts(scriptsData);
    } catch (error) {
      console.error('Error loading scripts:', error);
      toast.error('Error loading scripts');
    }
  };

  const loadData = async () => {
    // Campaigns are loaded via infinite scroll hook
    // Only load scripts here
    await loadScripts();
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length > 0) {
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          const data = lines.slice(1).map((line, index) => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const row = { id: index + 1 };
            headers.forEach((header, idx) => {
              row[header] = values[idx] || '';
            });
            return row;
          });

          setCsvData(data);
          setShowCsvPreview(true);

          // Intelligent automatic mapping
          // Intelligent automatic mapping — handles French CSV column names
          const autoMapping = {};
          headers.forEach(header => {
            const lowerHeader = header.toLowerCase().trim();
            const upperHeader = header.toUpperCase().trim();

            // Phone — must check BEFORE nom to avoid "TEL_MOBILE1" matching "nom"
            if (lowerHeader.includes('tel') || lowerHeader.includes('phone') || lowerHeader.includes('telephone') || lowerHeader.includes('mobile')) {
              autoMapping[header] = 'phone';
              // Prénom — must check BEFORE nom (so "prenom" doesn't fall into "nom")
            } else if (lowerHeader.includes('prénom') || lowerHeader.includes('prenom') || lowerHeader.includes('firstname') || lowerHeader === 'first_name') {
              autoMapping[header] = 'firstName';
              // Nom / Name
            } else if (lowerHeader === 'nom' || lowerHeader === 'name' || lowerHeader === 'last_name' || lowerHeader === 'lastname') {
              autoMapping[header] = 'name';
              // Email
            } else if (lowerHeader.includes('email') || lowerHeader.includes('mail')) {
              autoMapping[header] = 'email';
              // Address — handles ADR, ADR1-ADR9, ADRESSE, ADDRESS
            } else if (lowerHeader.startsWith('adr') || lowerHeader.includes('adresse') || lowerHeader.includes('address')
              // French list exports name the street column VOIE / RUE / LIEU-DIT / LIBELLE_VOIE —
              // none matched, so a 2026-08-24 import silently dropped every address.
              || lowerHeader.includes('voie') || lowerHeader.startsWith('rue') || lowerHeader.includes('lieu') || lowerHeader.includes('libelle')) {
              autoMapping[header] = 'address';
              // City — handles VILLE, CITY, COMMUNE, LOCALITÉ
            } else if (lowerHeader === 'ville' || lowerHeader.includes('ville') || lowerHeader.includes('city')
              || lowerHeader.includes('commune') || lowerHeader.includes('localit')) {
              autoMapping[header] = 'city';
              // Postal code — handles CP, CODE_POSTAL, POSTAL, CODEPOSTAL, ZIP
            } else if (upperHeader === 'CP' || lowerHeader === 'cp' || lowerHeader.includes('postal') || lowerHeader.includes('zip') || lowerHeader.includes('code_postal') || lowerHeader.includes('codepostal')) {
              autoMapping[header] = 'postal';
              // Civility
            } else if (lowerHeader === 'civilite' || lowerHeader === 'civilité' || lowerHeader === 'civ' || lowerHeader === 'titre') {
              autoMapping[header] = 'civilite';
            } else if (lowerHeader === 'id') {
              autoMapping[header] = 'id';
            }
          });

          setColumnMapping(autoMapping);
          toast.success(`${data.length} contacts imported with automatic mapping`);
          // Unmapped address/city is silent data loss: the sheet columns stay empty for
          // every lead of the campaign and nothing can backfill them later.
          const mappedFields = Object.values(autoMapping);
          const missing = [];
          if (!mappedFields.includes('address')) missing.push('Adresse');
          if (!mappedFields.includes('city')) missing.push('Ville');
          if (missing.length) {
            toast.warning(`⚠️ No column recognized for: ${missing.join(', ')}. ` +
              `Map ${missing.length > 1 ? 'them' : 'it'} manually below or those fields will stay EMPTY on the Google Sheet.`,
              { autoClose: 12000 });
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const processContacts = (withDuplication = false) => {
    const processedContacts = csvData.map(row => {
      const contact = {};
      Object.entries(columnMapping).forEach(([csvColumn, mappedField]) => {
        if (mappedField && row[csvColumn]) {
          contact[mappedField] = row[csvColumn];

          // ✅ FIX: Also store French field aliases so enrichmentService finds them
          // This means if CSV "ADR4" maps to "address", we ALSO store contact.adresse
          // and if "CP" maps to "postal", we ALSO store contact.code_postal
          const fieldAliases = {
            'address': ['adresse'],
            'postal': ['code_postal'],
            'city': ['ville'],
            'name': ['nom'],
            'firstName': ['prenom'],
          };
          if (fieldAliases[mappedField]) {
            fieldAliases[mappedField].forEach(alias => {
              if (!contact[alias]) contact[alias] = row[csvColumn];
            });
          }

          // Also preserve original CSV column name (lowercased) for script variable replacement
          const csvColumnLower = csvColumn.toLowerCase().replace(/[^a-z0-9_]/g, '_');
          if (!contact[csvColumnLower] && csvColumnLower !== mappedField.toLowerCase()) {
            contact[csvColumnLower] = row[csvColumn];
          }
        }
      });
      return contact;
    }).filter(contact => contact.phone);

    let uniqueContacts = processedContacts;
    let duplicateCount = 0;

    // Deduplication if without duplication mode
    if (!withDuplication) {
      const seen = new Set();
      uniqueContacts = [];

      processedContacts.forEach(contact => {
        const key = contact.phone;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueContacts.push(contact);
        } else {
          duplicateCount++;
        }
      });
    }

    setMappedContacts(uniqueContacts);

    if (!withDuplication && duplicateCount > 0) {
      toast.success(`${uniqueContacts.length} unique contacts processed. ${duplicateCount} duplicate contacts removed.`);
    } else if (!withDuplication) {
      toast.success(`${uniqueContacts.length} contacts processed (no duplicates found)`);
    } else {
      toast.success(`${uniqueContacts.length} contacts processed (with duplicates)`);
    }
  };

  const processContactsWithoutDuplication = () => {
    processContacts(false);
  };

  const processContactsWithDuplication = () => {
    processContacts(true);
  };

  const createCampaign = async () => {
    try {
      if (!campaignData.name || !campaignData.script) {
        toast.error('Name and script are required');
        return;
      }

      // Validate language selection
      if (!selectedLanguage && !campaignData.ai_config?.language) {
        toast.error('Please select a language first');
        return;
      }

      // Validate AI models
      if (!campaignData.ai_config?.stt_model) {
        toast.error('STT Model is required');
        return;
      }
      if (!campaignData.ai_config?.llm_model) {
        toast.error('LLM Model is required');
        return;
      }
      if (!campaignData.ai_config?.tts_model) {
        toast.error('TTS Model is required');
        return;
      }
      if (!campaignData.ai_config?.voice) {
        toast.error('Voice is required. Please select a voice for the TTS model.');
        return;
      }

      // Validate contacts
      if (!mappedContacts || mappedContacts.length === 0) {
        toast.error('CSV File with contacts is required');
        return;
      }

      setCreatingCampaign(true);
      if (mappedContacts.length > 10000) {
        toast.info(`Creating campaign with ${mappedContacts.length.toLocaleString()} contacts. This may take a moment...`, {
          autoClose: false,
          toastId: 'large-campaign-creation'
        });
      }

      const defaultCustomFields = cloneSampleItems(SAMPLE_CUSTOM_FIELDS);
      const defaultCallCategories = cloneSampleItems(SAMPLE_CALL_CATEGORIES);

      const response = await apiService.campaigns.create({
        name: campaignData.name,
        description: campaignData.description,
        script: campaignData.script,
        selected_script_id: campaignData.selected_script_id,
        conversation_mode: campaignData.conversation_mode || 'script',
        contactList: mappedContacts,
        contacts_count: mappedContacts.length,
        ai_config: {
          ...campaignData.ai_config,
          language: selectedLanguage || campaignData.ai_config?.language
        },
        sip_config: campaignData.sip_config,
        schedule_config: campaignData.schedule_config,
        background_ambiance: campaignData.background_ambiance,
        google_sheet_config: campaignData.google_sheet_config,
        concurrent_calls: campaignData.concurrent_calls || 5,
        amd_config: campaignData.amd_config,
        customFields: defaultCustomFields,
        callCategories: defaultCallCategories,
        faq_list: (campaignData.faq_list || []).filter(f => f.question && f.answer),
        faq_threshold: campaignData.faq_threshold || 0.70,
        lead_qualification: { ...DEFAULT_LEAD_QUALIFICATION, ...(campaignData.lead_qualification || {}) }
      });

      if (mappedContacts.length > 10000) toast.dismiss('large-campaign-creation');

      const newCampaign = response.data || response;
      const campaignId = newCampaign._id || newCampaign.id;

      if (campaignData.google_sheet_config?.sync_enabled && campaignId) {
        apiService.campaigns.update(campaignId, {
          google_sheet_config: {
            sheet_url: campaignData.google_sheet_config.sheet_url,
            sync_enabled: true
          }
        }).catch(sheetError => {
          console.warn('Google Sheets error:', sheetError);
          toast.warn('Campaign created but Google Sheets sync may be delayed');
        });
      }

      toast.success(`Campaign created successfully with ${mappedContacts.length.toLocaleString()} contacts!`);
      closeModal(setModalState, 'showCreate');
      resetForm();
      resetCampaigns();
    } catch (error) {
      console.error('Error creating campaign:', error);
      if (error.code === 'ECONNABORTED') {
        toast.warning('Campaign creation is taking longer than expected. Please check the campaigns list in a moment.', { autoClose: 8000 });
        closeModal(setModalState, 'showCreate');
        resetForm();
        resetCampaigns();
      } else {
        toast.error('Error creating campaign: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setCreatingCampaign(false);
      toast.dismiss('large-campaign-creation');
    }
  };

  const resetForm = () => {
    setCampaignData(resetCampaignData());
    setSelectedLanguage(''); // Reset language
    setCsvFile(null);
    setCsvData([]);
    setMappedContacts([]);
    setShowCsvPreview(false);
    setColumnMapping({});
    // setEnableDeduplication(false);
  };

  const startCampaign = async (campaignId) => {
    // Find the campaign to check its status
    const campaign = campaigns.find(c => c._id === campaignId);

    setStartingCampaignId(campaignId);
    try {
      await apiService.campaigns.start(campaignId);
      toast.success('Campaign started!');
      loadData();
    } catch (error) {
      console.error('Error starting campaign:', error);
      const serverError = error.response?.data?.error || '';
      const serverMessage = error.response?.data?.message || '';

      // Check if it's a schedule-related restriction (intentional restriction)
      const isScheduleRestriction = serverError.toLowerCase().includes('schedule');

      if (isScheduleRestriction) {
        toast.warning('Campaign cannot be started outside its scheduled time');
      } else {
        const errorMessage = serverMessage || serverError || 'Unable to start campaign. Please try again.';
        toast.error(errorMessage);
      }
    } finally {
      setStartingCampaignId(null);
    }
  };

  const pauseCampaign = async (campaignId) => {
    // Find the campaign to check its status
    const campaign = campaigns.find(c => c._id === campaignId);
    if (campaign && campaign.status === 'completed') {
      toast.error('This campaign is already completed and cannot be paused');
      return;
    }

    setPausingCampaignId(campaignId);
    try {
      await apiService.campaigns.pause(campaignId);
      toast.success('Campaign paused!');
      loadData();
    } catch (error) {
      console.error('Error pausing campaign:', error);
      const serverError = error.response?.data?.error || '';
      const serverMessage = error.response?.data?.message || '';

      // Check if it's a schedule-related restriction (intentional restriction)
      const isScheduleRestriction = serverError.toLowerCase().includes('schedule');

      if (isScheduleRestriction) {
        toast.warning('Campaign cannot be paused outside its scheduled time');
      } else {
        const errorMessage = serverMessage || serverError || 'Unable to pause campaign. Please try again.';
        toast.error(errorMessage);
      }
    } finally {
      setPausingCampaignId(null);
    }
  };

  /**
   * What is this campaign actually doing, in the user's terms?
   * `status` alone is ambiguous: a campaign between time slots and one the user
   * suspended both sit at execution_state 'paused'.
   */
  const describeCampaignState = (c) => {
    const sched = c?.schedule_config;
    const nextSlotText = () => {
      if (!sched?.enabled || !sched.time_slots?.length) return null;
      try {
        const now = new Date();
        const parisNow = new Date(now.toLocaleString('en-US', { timeZone: sched.timezone || 'Europe/Paris' }));
        const mins = parisNow.getHours() * 60 + parisNow.getMinutes();
        const starts = sched.time_slots
          .map(t => {
            const [h, m] = String(t.start || '').split(':').map(Number);
            return { txt: t.start, mins: (h || 0) * 60 + (m || 0) };
          })
          .sort((a, b) => a.mins - b.mins);
        const upcoming = starts.find(sl => sl.mins > mins);
        return upcoming ? `next run ${upcoming.txt}` : `next run ${starts[0]?.txt} tomorrow`;
      } catch { return null; }
    };

    if (c?.status === 'completed') return { label: 'completed', hint: null };
    if (c?.status === 'paused') return { label: 'suspended', hint: 'press Start to resume' };
    if (c?.status === 'active' && c?.execution_state === 'running') return { label: 'calling', hint: null };
    if (c?.status === 'active') return { label: 'scheduled', hint: nextSlotText() };
    return { label: c?.status || 'unknown', hint: null };
  };

  const showCampaignDetails = (campaign) => {
    setSelectedCampaign(normalizeCampaignConfig(campaign));
    openModal(setModalState, 'showDetail');
  };

  const editCampaign = (campaign) => {
    const normalized = normalizeCampaignConfig(campaign);
    setEditingCampaign(normalized);
    // Extract language from existing campaign or detect from models
    const campaignLanguage = normalized.ai_config?.language ||
      (normalized.ai_config?.stt_model?.includes('_fr') || normalized.ai_config?.llm_model?.includes('_fr') ? 'fr' :
        normalized.ai_config?.stt_model?.includes('_en') || normalized.ai_config?.llm_model?.includes('_en') ? 'en' : '');
    setSelectedLanguage(campaignLanguage);
    setCampaignData({
      name: normalized.name || '',
      description: normalized.description || '',
      script: normalized.script || '',
      selected_script_id: normalized.selected_script_id || null,
      conversation_mode: normalized.conversation_mode || 'script',
      ai_config: {
        ...(normalized.ai_config || DEFAULT_AI_CONFIG),
        language: campaignLanguage // Ensure language is set
      },
      sip_config: normalized.sip_config || DEFAULT_SIP_CONFIG,
      schedule_config: normalized.schedule_config || DEFAULT_SCHEDULE_CONFIG, // ✅ FIX: Include schedule_config
      background_ambiance: normalized.background_ambiance || 'none', // ✅ FIX: Include background_ambiance
      concurrent_calls: normalized.concurrent_calls || 5,
      amd_config: normalized.amd_config || DEFAULT_AMD_CONFIG,
      google_sheet_config: normalized.google_sheet_config || DEFAULT_GOOGLE_SHEET_CONFIG
    });
    openModal(setModalState, 'showEdit');
  };

  const updateCampaign = async () => {
    if (!editingCampaign) return;

    setLoading(true);
    try {
      const response = await apiService.campaigns.update(editingCampaign._id, campaignData);

      // Update campaign list
      setCampaigns(campaigns.map(c =>
        c._id === editingCampaign._id ? normalizeCampaignConfig(response.data) : c
      ));

      closeModal(setModalState, 'showEdit');
      setEditingCampaign(null);
      setSelectedLanguage(''); // Reset language
      toast.success('Campaign modified successfully!');

      // Reset the form
      setCampaignData(resetCampaignData());
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Error modifying campaign');
    } finally {
      setLoading(false);
    }
  };

  const exportCampaign = async (campaignId, format = 'xlsx') => {
    try {
      const response = await apiService.campaigns.export(campaignId, format);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campagne_${campaignId}.${format}`);

      // Check if link is already in DOM before appending
      if (!document.body.contains(link)) {
        document.body.appendChild(link);
      }

      link.click();

      // Clean up with error handling
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success(`${format.toUpperCase()} export downloaded!`);
    } catch (error) {
      console.error('Error exporting campaign:', error);
      toast.error('Error during export');
    }
  };

  const exportCampaignWithChoice = (campaignId) => {
    setExportingCampaignId(campaignId);
    openModal(setModalState, 'showExportFormat');
  };

  const handleExportFormat = (format) => {
    if (exportingCampaignId) {
      exportCampaign(exportingCampaignId, format);
      closeModal(setModalState, 'showExportFormat');
      setExportingCampaignId(null);
    }
  };

  const getRecordingCount = (campaign) => {
    if (!campaign) return 0;
    const counts = [
      campaign.calls_made,
      campaign.recordings_completed,
      campaign.current_contact_index,
      campaign.confirmed_calls
    ];

    const firstValid = counts.find((value) => typeof value === 'number' && !Number.isNaN(value));
    return typeof firstValid === 'number' ? firstValid : 0;
  };

  const getCampaignAnalytics = (campaign) => {
    if (!campaign) {
      return {
        minutes: 0,
        validatedLeads: 0,
        costPerLead: 0
      };
    }

    // Get validated leads count
    const validatedLeads = campaign.confirmed_calls || 0;

    // Get minutes consumed from analytics_snapshot.total_minutes, or calculate from call_cost
    let minutes = 0;
    if (campaign.analytics_snapshot?.total_minutes !== undefined && campaign.analytics_snapshot?.total_minutes !== null) {
      minutes = campaign.analytics_snapshot.total_minutes;
    } else if (campaign.analytics_snapshot?.call_cost && campaign.analytics_snapshot?.sip_cost_per_minute_used) {
      // Fallback: calculate from call_cost if total_minutes not available
      minutes = campaign.analytics_snapshot.call_cost / campaign.analytics_snapshot.sip_cost_per_minute_used;
    }

    // Calculate call_cost from available data
    // Prefer analytics_snapshot.call_cost, fallback to calculation from total_minutes * sip_cost_per_minute
    let callCost = 0;
    if (campaign.analytics_snapshot?.call_cost !== undefined && campaign.analytics_snapshot?.call_cost !== null) {
      callCost = campaign.analytics_snapshot.call_cost;
    } else if (minutes > 0 && campaign.analytics_snapshot?.sip_cost_per_minute_used) {
      // Fallback: calculate from total_minutes * sip_cost_per_minute
      callCost = minutes * campaign.analytics_snapshot.sip_cost_per_minute_used;
    }

    // Calculate cost_per_lead on frontend for real-time accuracy
    // If confirmed_calls > 0: cost_per_lead = call_cost / confirmed_calls
    // If confirmed_calls = 0: cost_per_lead = 0 (avoid division by zero)
    let costPerLead = 0;
    if (validatedLeads > 0 && callCost > 0) {
      costPerLead = callCost / validatedLeads;
    }

    return {
      minutes: parseFloat(minutes.toFixed(2)),
      validatedLeads,
      costPerLead: parseFloat(costPerLead.toFixed(2))
    };
  };

  const deleteCampaign = (campaignId) => {
    const campaign = campaigns.find(c => c._id === campaignId);
    setCampaignToDelete(campaign);
    openModal(setModalState, 'showDeleteConfirm');
  };

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    try {
      await apiService.campaigns.delete(campaignToDelete._id);
      setCampaigns(campaigns.filter(c => c._id !== campaignToDelete._id));
      toast.success('Campaign deleted successfully!');
      closeModal(setModalState, 'showDeleteConfirm');
      setCampaignToDelete(null);
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Error during deletion');
    }
  };

  const handleScriptSelection = (scriptId) => {
    const selectedScript = scripts.find(s => s._id === scriptId);
    if (selectedScript) {
      setCampaignData({
        ...campaignData,
        selected_script_id: scriptId,
        script: selectedScript.content,
        // ✅ Auto-populate FAQs from script
        faq_list: selectedScript.faq_list && selectedScript.faq_list.length > 0
          ? selectedScript.faq_list
          : campaignData.faq_list || []
      });
      // Notify user if FAQs were loaded
      if (selectedScript.faq_list && selectedScript.faq_list.length > 0) {
        toast.success(`✅ ${selectedScript.faq_list.length} FAQ loaded from script`);
      }
    }
  };

  // Contact management functions
  const handleAdditionalCSVImport = async (file) => {
    if (!editingCampaign) return;

    try {
      // Read and parse the CSV file
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());

      // Create contacts with the same format as campaign creation
      const contacts = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const contact = {};

        headers.forEach((header, index) => {
          const lowerHeader = header.toLowerCase();
          if (lowerHeader.includes('nom') && !lowerHeader.includes('prenom')) {
            contact['name'] = values[index];
          } else if (lowerHeader.includes('prenom') || lowerHeader.includes('prénom')) {
            contact['firstName'] = values[index];
          } else if (lowerHeader.includes('phone') || lowerHeader.includes('telephone')) {
            contact['phone'] = values[index];
          } else if (lowerHeader.includes('email')) {
            contact['email'] = values[index];
          } else if (lowerHeader.includes('ville') || lowerHeader.includes('city')) {
            contact['city'] = values[index];
          } else if (lowerHeader.includes('adresse') || lowerHeader.includes('address')) {
            contact['address'] = values[index];
          } else if (lowerHeader.includes('postal') || lowerHeader.includes('code')) {
            contact['postal'] = values[index];
          }
        });

        if (contact.phone) { // Only if phone exists
          contacts.push(contact);
        }
      }

      // Use the same endpoint as campaign creation
      const response = await apiService.contacts.addToCampaign(editingCampaign._id, {
        contacts: contacts
      });

      toast.success(`${contacts.length} contacts added!`);
    } catch (error) {
      console.error('Error importing contacts:', error);
      toast.error('Error importing contacts');
    }
  };

  const addManualContact = async () => {
    if (!editingCampaign || !newContact.phone) return;

    try {
      const contactData = {
        campaign_id: editingCampaign._id,
        ...newContact
      };

      const response = await apiService.contacts.create(contactData);
      toast.success('Contact added successfully!');

      // Reset the form
      setNewContact(resetNewContact());
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Error adding contact');
    }
  };

  // Data enrichment functions
  const openEnrichmentConfig = (campaign) => {
    const normalized = normalizeCampaignConfig(campaign);
    setEditingCampaign(normalized);
    const fieldsToUse = normalized.customFields.length > 0
      ? normalized.customFields
      : cloneSampleItems(SAMPLE_CUSTOM_FIELDS);
    const categoriesToUse = normalized.callCategories.length > 0
      ? normalized.callCategories
      : cloneSampleItems(SAMPLE_CALL_CATEGORIES);
    setCustomFields(fieldsToUse);
    setCallCategories(categoriesToUse);
    openModal(setModalState, 'showEnrichment');
  };

  const addCustomField = () => {
    const newField = {
      id: Date.now().toString(),
      name: '',
      label: '',
      type: 'text',
      description: '',
      required: false,
      options: []
    };
    setCustomFields([...customFields, newField]);
  };

  const updateCustomField = (index, field) => {
    const updated = [...customFields];
    updated[index] = field;
    setCustomFields(updated);
  };

  const removeCustomField = (index) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  const addCallCategory = () => {
    const newCategory = {
      id: '',
      name: '',
      description: '',
      color: '#3B82F6',
      lead_outcome: 'neutral'
    };
    setCallCategories([...callCategories, newCategory]);
  };

  const updateCallCategory = (index, category) => {
    const updated = [...callCategories];
    updated[index] = category;
    setCallCategories(updated);
  };

  const removeCallCategory = (index) => {
    setCallCategories(callCategories.filter((_, i) => i !== index));
  };

  const saveEnrichmentConfig = async () => {
    if (!editingCampaign) return;

    setSavingEnrichment(true);
    try {
      const response = await apiService.campaigns.updateCustomFields(editingCampaign._id, {
        custom_fields: customFields,
        // Persist the outcome the select displays, so a pre-migration category keeps its
        // prefix default instead of falling to the schema default on save.
        call_categories: callCategories.map(cat => ({ ...cat, lead_outcome: cat.lead_outcome || defaultLeadOutcome(cat.id) })),
        enrichment_config: {
          enabled: true,
          extraction_prompt: "You are an AI assistant specialized in extracting information from commercial calls.",
          categorization_prompt: "Analyze this conversation and categorize the call according to available categories."
        },
        lead_qualification: { ...DEFAULT_LEAD_QUALIFICATION, ...(editingCampaign.lead_qualification || {}) }
      });

      toast.success('Enrichment configuration saved!');
      closeModal(setModalState, 'showEnrichment');

      // Update campaign in the list
      const updatedCampaign = normalizeCampaignConfig(response.data?.campaign || editingCampaign);
      const syncedCampaign = {
        ...updatedCampaign,
        status: 'active',
        execution_state: updatedCampaign.execution_state || 'idle'
      };

      setCampaigns(campaigns.map(c =>
        c._id === syncedCampaign._id ? syncedCampaign : c
      ));

      setEditingCampaign(syncedCampaign);
      setSelectedCampaign(prev =>
        prev && prev._id === syncedCampaign._id ? syncedCampaign : prev
      );

    } catch (error) {
      console.error('Error saving enrichment config:', error);
      toast.error('Error saving configuration');
    } finally {
      setSavingEnrichment(false);
    }
  };

  const openRecycle = async (campaign) => {
    try {
      const r = await apiService.campaigns.recycleOptions(campaign._id);
      if (!r.data?.success) { toast.error('Could not load recycle options'); return; }
      setRecycleModal({
        campaign, options: r.data, cats: [], outs: [],
        // Default to recycling in place: the client asked "why create a new
        // campaign? in the same campaign it recycles contacts" (2026-08-21).
        mode: 'same',
        name: `${campaign.name} — recyclage`, creating: false,
      });
    } catch (e) {
      toast.error('Could not load recycle options');
    }
  };

  const submitRecycle = async () => {
    const mdl = recycleModal;
    if (!mdl || mdl.creating) return;
    if (!mdl.cats.length && !mdl.outs.length) { toast.warning('Select at least one category'); return; }
    setRecycleModal(prev => ({ ...prev, creating: true }));
    try {
      const r = await apiService.campaigns.recycle(mdl.campaign._id, {
        categories: mdl.cats, outcomes: mdl.outs, name: mdl.name, mode: mdl.mode,
      });
      if (r.data?.success) {
        toast.success(r.data.mode === 'same'
          ? `♻️ ${r.data.contacts} contacts re-armed in "${mdl.campaign.name}" — they will be called again on the next time slot.`
          : `♻️ "${r.data.name}" created with ${r.data.contacts} contacts — review it, then press Start.`);
        setRecycleModal(null);
        resetCampaigns();
      } else {
        toast.error(r.data?.message || 'Recycling failed');
        setRecycleModal(prev => ({ ...prev, creating: false }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Recycling failed');
      setRecycleModal(prev => ({ ...prev, creating: false }));
    }
  };

  const regenerateTts = async (campaign) => {
    if (regeneratingTtsId) return;
    const ok = window.confirm(
      `Regenerate all voice audio for "${campaign.name}"?\n\n` +
      `Every line will be re-created with the current voice` +
      `${campaign.ai_config?.voice ? ` (${campaign.ai_config.voice})` : ''}.\n` +
      `This takes a few minutes and the campaign cannot start until it finishes.\n\n` +
      `Use this if you hear a wrong sentence in a call, or after changing the voice.`
    );
    if (!ok) return;
    setRegeneratingTtsId(campaign._id);
    try {
      const response = await apiService.campaigns.regenerateTts(campaign._id);
      if (response.data?.success) {
        // Reflect "in progress" immediately — the socket events then drive the
        // progress bar that the Start button already renders.
        setCampaigns(prev => prev.map(c =>
          c._id === campaign._id ? { ...c, tts_pregen_status: 'in_progress' } : c));
        toast.success(`🎙️ Regenerating voice for "${campaign.name}" — follow the progress on the Start button.`);
      } else {
        toast.error(response.data?.message || 'Failed to start regeneration');
      }
    } catch (error) {
      const msg = error?.response?.status === 409
        ? 'Voice generation is already running for this campaign'
        : (error?.response?.data?.message || 'Error starting voice regeneration');
      toast.error(msg);
    } finally {
      setRegeneratingTtsId(null);
    }
  };

  const clearTtsCache = async (campaign) => {
    if (clearingCacheId) return;
    setClearingCacheId(campaign._id);
    try {
      const response = await apiService.tts.clearCache(campaign._id);
      if (response.data?.success) {
        toast.success(`🗑️ Voice cache cleared for "${campaign.name}" — fresh audio will be generated on next run!`);
      } else {
        toast.error('Failed to clear cache');
      }
    } catch (error) {
      console.error('Error clearing TTS cache:', error);
      toast.error('Error clearing voice cache');
    } finally {
      setClearingCacheId(null);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Call Campaigns</h1>
          <button
            onClick={() => openModal(setModalState, 'showCreate')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + New Campaign
          </button>
        </div>

        {/* Campaign list */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Active Campaigns</h2>
            <p className="text-sm text-gray-600">AI-powered call campaign management</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" style={{ width: '100%', tableLayout: 'fixed' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '20%' }}>Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>Script</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%' }}>Contacts</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '8%' }}>Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '18%' }}>Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>Recordings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '14%' }}>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => {
                  const analytics = getCampaignAnalytics(campaign);
                  return (
                    <tr key={campaign._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                          onClick={() => showCampaignDetails(campaign)}
                          title="Click to view campaign details"
                        >
                          {campaign.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 truncate">{campaign.description}</div>
                        <div className="text-xs text-gray-500 mt-2 whitespace-nowrap">
                          ⏱️ {analytics.minutes.toFixed(1)} min | ✅ {analytics.validatedLeads} leads | 💰 €{analytics.costPerLead.toFixed(2)}/lead
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 truncate">{campaign.script}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">{campaign.contacts_count || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {campaign.status === 'active' && (
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                          )}
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                            campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                            {describeCampaignState(campaign).label}
                          </span>
                          {/* Two very different situations used to render identically:
                              suspended by the user (never resumes on its own) vs simply
                              between time slots (resumes automatically). With no way to
                              tell them apart, the client deleted a live campaign because
                              he could not confirm it had actually stopped. */}
                          {describeCampaignState(campaign).hint && (
                            <span className="text-xs text-gray-500 ml-1" title={describeCampaignState(campaign).hint}>
                              {describeCampaignState(campaign).hint}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {/* calls_made is cumulative across runs, so after a recycle it
                            still read "10217/10225" and looked finished while thousands
                            of contacts were queued. Drive this from the contacts that
                            actually remain to dial. */}
                        {(() => {
                          const total = campaign.contacts_count ?? 0;
                          const pending = campaign.pending_count;
                          const done = campaign.done_count ?? Math.min(campaign.calls_made ?? 0, total);
                          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                          if (!total) return <div className="text-sm text-gray-900">{campaign.calls_made ?? 0}</div>;
                          return (
                            <div>
                              <div className="text-sm text-gray-900 whitespace-nowrap">
                                {done.toLocaleString()} / {total.toLocaleString()}
                                <span className="text-gray-500"> ({pct}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full mt-1" style={{ height: '6px' }}>
                                <div className="bg-blue-500 rounded-full" style={{ height: '6px', width: `${pct}%` }} />
                              </div>
                              {pending > 0 && (
                                <div className="text-xs text-blue-600 mt-1 whitespace-nowrap">
                                  {pending.toLocaleString()} à appeler
                                </div>
                              )}
                              {pending === 0 && (
                                <div className="text-xs text-gray-400 mt-1 whitespace-nowrap">liste terminée</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">🎵 {campaign.recordings_count ?? 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => startCampaign(campaign._id)}
                            disabled={startingCampaignId === campaign._id || pausingCampaignId === campaign._id || ['pending', 'in_progress'].includes(campaign.tts_pregen_status)}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${startingCampaignId === campaign._id || pausingCampaignId === campaign._id || ['pending', 'in_progress'].includes(campaign.tts_pregen_status)
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : campaign.status === 'completed' ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'
                              }`}
                            title={['pending', 'in_progress'].includes(campaign.tts_pregen_status) ? 'Audio pre-generation in progress...' : startingCampaignId === campaign._id ? 'Starting campaign...' : campaign.status === 'completed' ? 'Restart campaign from scratch' : 'Start campaign'}
                          >
                            {['pending', 'in_progress'].includes(campaign.tts_pregen_status) ? (
                              <span className="flex flex-col items-center gap-0.5" style={{ minWidth: '80px' }}>
                                <span className="flex items-center gap-1">
                                  🔄 {pregenProgress[campaign._id]
                                    ? `${pregenProgress[campaign._id].completed}/${pregenProgress[campaign._id].total}`
                                    : 'Preparing...'}
                                </span>
                                {pregenProgress[campaign._id] && pregenProgress[campaign._id].total > 0 && (
                                  <span className="w-full bg-gray-300 rounded-full" style={{ height: '4px' }}>
                                    <span
                                      className="block bg-blue-500 rounded-full transition-all duration-300"
                                      style={{
                                        height: '4px',
                                        width: `${Math.round((pregenProgress[campaign._id].completed / pregenProgress[campaign._id].total) * 100)}%`
                                      }}
                                    />
                                  </span>
                                )}
                              </span>
                            ) : startingCampaignId === campaign._id ? '⏳ Starting...' : campaign.status === 'completed' ? '🔄 Restart' : '▶️ Start'}
                          </button>
                          <button
                            onClick={() => pauseCampaign(campaign._id)}
                            disabled={campaign.status === 'completed' || pausingCampaignId === campaign._id || startingCampaignId === campaign._id}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${campaign.status === 'completed' || pausingCampaignId === campaign._id || startingCampaignId === campaign._id
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                              }`}
                            title={campaign.status === 'completed' ? 'Campaign is already completed' : pausingCampaignId === campaign._id ? 'Pausing campaign...' : 'Pause'}
                          >
                            {pausingCampaignId === campaign._id ? '⏳ Pausing...' : '⏸️ Pause'}
                          </button>
                          <button
                            onClick={() => showCampaignDetails(campaign)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors"
                            title="View details"
                          >
                            👁️ Details
                          </button>
                          <button
                            onClick={() => editCampaign(campaign)}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors"
                            title="Edit campaign"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => openEnrichmentConfig(campaign)}
                            className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors"
                            title="Enrichment configuration"
                          >
                            🧠 Enrichment
                          </button>
                          <button
                            onClick={() => exportCampaignWithChoice(campaign._id)}
                            className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors"
                            title="Export to XLSX/CSV"
                          >
                            📊 Export
                          </button>
                          <button
                            onClick={() => openRecycle(campaign)}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors"
                            title="Recycle contacts by call category into a follow-up campaign"
                          >
                            ♻️ Recycle
                          </button>
                          <button
                            onClick={() => regenerateTts(campaign)}
                            disabled={regeneratingTtsId === campaign._id || ['pending', 'in_progress'].includes(campaign.tts_pregen_status)}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                              regeneratingTtsId === campaign._id || ['pending', 'in_progress'].includes(campaign.tts_pregen_status)
                                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                            }`}
                            title="Regenerate all voice audio with the current voice — use after changing the voice, or if a line sounds wrong"
                          >
                            {regeneratingTtsId === campaign._id ? '⏳ Starting...' : '🎙️ Regenerate Voice'}
                          </button>
                          <button
                            onClick={() => clearTtsCache(campaign)}
                            disabled={clearingCacheId === campaign._id}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                              clearingCacheId === campaign._id
                                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                : 'bg-teal-500 hover:bg-teal-600 text-white'
                            }`}
                            title="Clear pre-generated voice cache — forces fresh audio on next run"
                          >
                            {clearingCacheId === campaign._id ? '⏳ Clearing...' : '🔄 Clear Cache'}
                          </button>
                          <button
                            onClick={() => deleteCampaign(campaign._id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors"
                            title="Delete campaign"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Infinite scroll observer target */}
            {hasMoreCampaigns && (
              <div ref={campaignsObserverTarget} className="h-10 flex items-center justify-center py-4">
                {loadingCampaigns && <div className="text-gray-500">Loading more campaigns...</div>}
              </div>
            )}
          </div>
        </div>

        {/* Create modal */}
        {modalState.showCreate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Create New Campaign</h2>
                <button
                  onClick={() => closeModal(setModalState, 'showCreate')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Campaign Name *
                    </label>
                    <input
                      type="text"
                      value={campaignData.name}
                      onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="My VoxiPro AI Campaign"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={campaignData.description}
                      onChange={(e) => setCampaignData({ ...campaignData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Campaign description"
                    />
                  </div>
                </div>

                {/* Script selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Script to Use *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <select
                        value={campaignData.selected_script_id}
                        onChange={(e) => handleScriptSelection(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select existing script</option>
                        {scripts.map(script => (
                          <option key={script._id} value={script._id}>{script.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <textarea
                        value={campaignData.script}
                        onChange={(e) => setCampaignData({ ...campaignData, script: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Or type your custom script here..."
                      />
                    </div>
                  </div>
                </div>

                {/* AI Configuration */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-3">AI Configuration</h3>

                  {/* Conversation Mode */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mode de conversation
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setCampaignData(prev => ({ ...prev, conversation_mode: 'script' }))}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          (campaignData.conversation_mode || 'script') === 'script'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Script (ligne par ligne)
                      </button>
                      <button
                        type="button"
                        onClick={() => setCampaignData(prev => ({ ...prev, conversation_mode: 'llm' }))}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          campaignData.conversation_mode === 'llm'
                            ? 'bg-purple-600 text-white border-purple-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        IA conversationnelle (LLM)
                      </button>
                    </div>
                    {campaignData.conversation_mode === 'llm' && (
                      <p className="mt-1 text-xs text-purple-600">
                        L'IA utilisera le script comme guide et répondra naturellement aux questions hors-script.
                      </p>
                    )}
                  </div>

                  {/* Language Selection - Must be selected first */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language *
                    </label>
                    <select
                      value={selectedLanguage || campaignData.ai_config?.language || ''}
                      onChange={(e) => {
                        setSelectedLanguage(e.target.value);
                        setCampaignData(prev => ({
                          ...prev,
                          ai_config: {
                            ...(prev.ai_config || DEFAULT_AI_CONFIG),
                            language: e.target.value
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Language First...</option>
                      <option value="en">English</option>
                      <option value="fr">French</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select language first to filter available models
                    </p>
                  </div>

                  {(selectedLanguage || campaignData.ai_config?.language) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">STT Model *</label>
                        <select
                          value={campaignData.ai_config?.stt_model || ''}
                          onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            ai_config: {
                              ...(prev.ai_config || DEFAULT_AI_CONFIG),
                              stt_model: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!selectedLanguage && !campaignData.ai_config?.language}
                        >
                          <option value="">Select Model</option>
                          {getFilteredModels('stt')
                            ?.filter(model => model.status === 'running' || model.status === 'available')
                            .map(model => (
                              <option key={model.id} value={model.id}>{model.name}</option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">LLM Model *</label>
                        <select
                          value={campaignData.ai_config?.llm_model || ''}
                          onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            ai_config: {
                              ...(prev.ai_config || DEFAULT_AI_CONFIG),
                              llm_model: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!selectedLanguage && !campaignData.ai_config?.language}
                        >
                          <option value="">Select Model</option>
                          {getFilteredModels('llm')
                            ?.filter(model => model.status === 'running' || model.status === 'available')
                            .map(model => (
                              <option key={model.id} value={model.id}>{model.name}</option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">TTS Model *</label>
                        <select
                          value={campaignData.ai_config?.tts_model || ''}
                          onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            ai_config: {
                              ...(prev.ai_config || DEFAULT_AI_CONFIG),
                              tts_model: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!selectedLanguage && !campaignData.ai_config?.language}
                        >
                          <option value="">Select Model</option>
                          {getFilteredModels('tts')
                            ?.filter(model => model.status === 'running' || model.status === 'available')
                            .map(model => (
                              <option key={model.id} value={model.id}>{model.name}</option>
                            ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Voice Selection */}
                  {campaignData.ai_config?.tts_model && availableVoices.length > 0 && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Voice *</label>
                      <div className="flex items-center gap-2">
                        <select
                          value={campaignData.ai_config?.voice || ''}
                          onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            ai_config: {
                              ...(prev.ai_config || DEFAULT_AI_CONFIG),
                              voice: e.target.value
                            }
                          }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Voice</option>
                          {availableVoices.map((voice) => (
                            <option key={voice.id} value={voice.id}>
                              {voice.name} ({voice.language || voice.provider || 'N/A'})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={testVoice}
                          disabled={!campaignData.ai_config?.voice || availableVoices.length === 0}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          🔊 Test
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Select a voice for the TTS model
                      </p>
                    </div>
                  )}

                  {/* TTS Speed Control */}
                  {campaignData.ai_config?.tts_model && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Speech Speed: <span className="text-blue-600 font-semibold">{(campaignData.ai_config?.tts_speed || 1.0).toFixed(2)}x</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">0.5x</span>
                        <input
                          type="range"
                          min="0.50"
                          max="1.50"
                          step="0.05"
                          value={campaignData.ai_config?.tts_speed || 1.0}
                          onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            ai_config: {
                              ...(prev.ai_config || DEFAULT_AI_CONFIG),
                              tts_speed: parseFloat(e.target.value)
                            }
                          }))}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <span className="text-xs text-gray-500">1.5x</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Adjust voice speed — lower = slower/calmer, higher = faster
                      </p>
                    </div>
                  )}
                </div>

                {/* SIP Configuration */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-3">SIP Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SIP Provider *
                      </label>
                      <select
                        value={campaignData.sip_config.provider}
                        onChange={(e) => setCampaignData({
                          ...campaignData,
                          sip_config: { ...campaignData.sip_config, provider: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sélectionnez un fournisseur SIP</option>
                        {availableSipProviders.map((provider) => (
                          <option
                            key={provider.id}
                            value={provider.id}
                            disabled={!provider.enabled && provider.status !== 'available'}
                          >
                            {provider.name} {!provider.enabled && provider.status !== 'available' ? '(Désactivé)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Caller ID
                      </label>
                      <input
                        type="text"
                        value={campaignData.sip_config.caller_id}
                        onChange={(e) => setCampaignData({
                          ...campaignData,
                          sip_config: { ...campaignData.sip_config, caller_id: e.target.value }
                        })}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${campaignData.sip_config.caller_id === '+33000000000' ? 'border-orange-400' : 'border-gray-300'}`}
                        placeholder="+33XXXXXXXXX"
                      />
                      {campaignData.sip_config.caller_id === '+33000000000' && (
                        <p className="text-xs text-orange-600 mt-1">
                          ⚠️ +33000000000 est signalé comme spam par les opérateurs français — les appels iront directement en messagerie. Utilisez un vrai numéro français.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Concurrent Calls *
                      </label>
                      <input
                        type="text"
                        value={campaignData.concurrent_calls || ''}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow empty input for better UX
                          if (inputValue === '') {
                            setCampaignData({
                              ...campaignData,
                              concurrent_calls: ''
                            });
                            return;
                          }
                          // Strip non-numeric characters
                          const numericValue = inputValue.replace(/\D/g, '');
                          if (numericValue === '') {
                            setCampaignData({
                              ...campaignData,
                              concurrent_calls: ''
                            });
                            return;
                          }
                          // Parse and clamp between 1 and 50
                          const parsedValue = parseInt(numericValue, 10);
                          if (!isNaN(parsedValue)) {
                            const clampedValue = Math.max(1, Math.min(50, parsedValue));
                            setCampaignData({
                              ...campaignData,
                              concurrent_calls: clampedValue
                            });
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="5"
                      />
                      <p className="text-xs text-gray-500 mt-1">Number of concurrent calls (1-50)</p>
                    </div>
                  </div>
                </div>

                {/* FAQ / Objection Handling */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-3">❓ FAQ / Objection Handling</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Add common questions users might ask during the call. If a user asks a matching question, the agent will answer it and then repeat the current script line.
                  </p>

                  {/* Match Threshold Slider */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Match Threshold: {((campaignData.faq_threshold || 0.70) * 100).toFixed(0)}%
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="100"
                      step="5"
                      value={(campaignData.faq_threshold || 0.70) * 100}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, faq_threshold: parseInt(e.target.value) / 100 }))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>50% (Loose)</span>
                      <span>75% (Balanced)</span>
                      <span>100% (Strict)</span>
                    </div>
                  </div>

                  {/* FAQ List */}
                  <div className="space-y-3">
                    {(campaignData.faq_list || []).map((faq, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200 relative">
                        <button
                          type="button"
                          onClick={() => {
                            const updatedList = [...(campaignData.faq_list || [])];
                            updatedList.splice(index, 1);
                            setCampaignData(prev => ({ ...prev, faq_list: updatedList }));
                          }}
                          className="absolute top-2 right-2 text-red-400 hover:text-red-600 text-sm font-bold"
                          title="Remove"
                        >
                          ✕
                        </button>
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Question {index + 1}</label>
                          <input
                            type="text"
                            value={faq.question || ''}
                            onChange={(e) => {
                              const updatedList = [...(campaignData.faq_list || [])];
                              updatedList[index] = { ...updatedList[index], question: e.target.value };
                              setCampaignData(prev => ({ ...prev, faq_list: updatedList }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="e.g., C'est quoi le but de cet appel ?"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Answer</label>
                          <textarea
                            value={faq.answer || ''}
                            onChange={(e) => {
                              const updatedList = [...(campaignData.faq_list || [])];
                              updatedList[index] = { ...updatedList[index], answer: e.target.value };
                              setCampaignData(prev => ({ ...prev, faq_list: updatedList }));
                            }}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="e.g., Nous vous contactons pour vérifier votre éligibilité aux aides aux économies d'énergie."
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => {
                        const updatedList = [...(campaignData.faq_list || []), { question: '', answer: '' }];
                        setCampaignData(prev => ({ ...prev, faq_list: updatedList }));
                      }}
                      className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors text-sm font-medium"
                    >
                      + Add Question & Answer
                    </button>
                  </div>

                  {(campaignData.faq_list || []).length > 0 && (
                    <p className="text-xs text-green-600 mt-2">
                      ✅ {(campaignData.faq_list || []).filter(f => f.question && f.answer).length} FAQ entries configured — answers will be pre-generated as audio
                    </p>
                  )}
                </div>

                {/* AMD Configuration (Answering Machine Detection) */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-3">🎯 Answering Machine Detection (AMD)</h3>
                  <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <div className="space-y-4">
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={campaignData.amd_config.enabled}
                            onChange={(e) => setCampaignData({
                              ...campaignData,
                              amd_config: {
                                ...campaignData.amd_config,
                                enabled: e.target.checked
                              }
                            })}
                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">Enable answering machine detection</span>
                            <p className="text-xs text-gray-600">Avoid unnecessary costs by automatically detecting answering machines</p>
                          </div>
                        </label>
                      </div>

                      {campaignData.amd_config.enabled && (
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">⚙️ AMD Settings (Optimized for France)</h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Initial silence (ms)
                              </label>
                              <input
                                type="number"
                                min="1000"
                                max="5000"
                                value={campaignData.amd_config.initial_silence}
                                onChange={(e) => setCampaignData({
                                  ...campaignData,
                                  amd_config: {
                                    ...campaignData.amd_config,
                                    initial_silence: parseInt(e.target.value) || DEFAULT_AMD_CONFIG.initial_silence
                                  }
                                })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-500">Optimized for French operators</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Greeting message (ms)
                              </label>
                              <input
                                type="number"
                                min="500"
                                max="3000"
                                value={campaignData.amd_config.greeting}
                                onChange={(e) => setCampaignData({
                                  ...campaignData,
                                  amd_config: {
                                    ...campaignData.amd_config,
                                    greeting: parseInt(e.target.value) || DEFAULT_AMD_CONFIG.greeting
                                  }
                                })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Max analysis time (ms)
                              </label>
                              <input
                                type="number"
                                min="3000"
                                max="10000"
                                value={campaignData.amd_config.total_analysis_time}
                                onChange={(e) => setCampaignData({
                                  ...campaignData,
                                  amd_config: {
                                    ...campaignData.amd_config,
                                    total_analysis_time: parseInt(e.target.value) || DEFAULT_AMD_CONFIG.total_analysis_time
                                  }
                                })}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                            <p className="text-xs text-yellow-800">
                              ⚠️ <strong>Important:</strong> AMD adds 5-6s per call and generates 15-25% false positives depending on operator.
                              Mixed mode (SIP signaling + voice analysis) recommended for 99.9% accuracy.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Configuration Google Sheets */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-4">Google Sheets Configuration</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={campaignData.google_sheet_config.sync_enabled}
                          onChange={(e) => setCampaignData({
                            ...campaignData,
                            google_sheet_config: {
                              ...campaignData.google_sheet_config,
                              sync_enabled: e.target.checked
                            }
                          })}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Enable Google Sheets synchronization</span>
                      </label>
                    </div>

                    {campaignData.google_sheet_config.sync_enabled && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Google Sheets URL
                        </label>
                        <input
                          type="url"
                          value={campaignData.google_sheet_config.sheet_url}
                          onChange={(e) => setCampaignData({
                            ...campaignData,
                            google_sheet_config: {
                              ...campaignData.google_sheet_config,
                              sheet_url: e.target.value
                            }
                          })}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            campaignData.google_sheet_config.sheet_url &&
                            !campaignData.google_sheet_config.sheet_url.startsWith('https://docs.google.com/spreadsheets/d/')
                              ? 'border-red-400' : 'border-gray-300'
                          }`}
                          placeholder="https://docs.google.com/spreadsheets/d/..."
                        />
                        {campaignData.google_sheet_config.sheet_url &&
                          !campaignData.google_sheet_config.sheet_url.startsWith('https://docs.google.com/spreadsheets/d/') && (
                          <p className="text-xs text-red-600 mt-1">
                            ❌ URL invalide — doit commencer par https://docs.google.com/spreadsheets/d/
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Collez l'URL du Google Sheet partagé ici. Les leads seront exportés automatiquement.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* NOUVEAUTÉ 1 - Gestion des Horaires de Campagne */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-4">🕐 Campaign Schedule</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={campaignData.schedule_config.enabled}
                          onChange={(e) => setCampaignData({
                            ...campaignData,
                            schedule_config: {
                              ...campaignData.schedule_config,
                              enabled: e.target.checked
                            }
                          })}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Select Time Slot</span>
                          <p className="text-xs text-gray-600">Limit calls to specific time slots</p>
                        </div>
                      </label>
                    </div>

                    {campaignData.schedule_config.enabled && (
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-3">⚙️ Schedule Configuration</h4>

                        {/* Timezone */}
                        <div className="mb-4">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Timezone
                          </label>
                          <select
                            value={campaignData.schedule_config.timezone}
                            onChange={(e) => setCampaignData({
                              ...campaignData,
                              schedule_config: {
                                ...campaignData.schedule_config,
                                timezone: e.target.value
                              }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="Europe/Paris">France (Europe/Paris)</option>
                            <option value="Asia/Kolkata">India (Asia/Kolkata)</option>
                            <option value="Europe/London">United Kingdom (Europe/London)</option>
                            <option value="Europe/Berlin">Germany (Europe/Berlin)</option>
                            <option value="Europe/Rome">Italy (Europe/Rome)</option>
                            <option value="Europe/Madrid">Spain (Europe/Madrid)</option>
                            <option value="America/New_York">United States East (America/New_York)</option>
                            <option value="America/Los_Angeles">United States West (America/Los_Angeles)</option>
                          </select>
                        </div>

                        {/* Days of the week */}
                        <div className="mb-4">
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Allowed call days
                          </label>
                          <div className="grid grid-cols-7 gap-2">
                            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => {
                              const dayNumber = index + 1;
                              const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                              return (
                                <label key={dayNumber} className="flex flex-col items-center" title={dayNames[index]}>
                                  <input
                                    type="checkbox"
                                    checked={campaignData.schedule_config.days_of_week.includes(dayNumber)}
                                    onChange={(e) => {
                                      const newDays = e.target.checked
                                        ? [...campaignData.schedule_config.days_of_week, dayNumber]
                                        : campaignData.schedule_config.days_of_week.filter(d => d !== dayNumber);
                                      setCampaignData({
                                        ...campaignData,
                                        schedule_config: {
                                          ...campaignData.schedule_config,
                                          days_of_week: newDays
                                        }
                                      });
                                    }}
                                    className="mb-1"
                                  />
                                  <span className="text-xs text-gray-700">{day}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        {/* Multiple Time Slots */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <label className="block text-xs font-medium text-gray-700">
                              Time Slots (24H Format) *
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const newSlots = [
                                  ...campaignData.schedule_config.time_slots,
                                  { start: '09:00', end: '17:00' }
                                ];
                                setCampaignData({
                                  ...campaignData,
                                  schedule_config: {
                                    ...campaignData.schedule_config,
                                    time_slots: newSlots
                                  }
                                });
                              }}
                              className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              + Add Time Slot
                            </button>
                          </div>

                          <div className="space-y-3">
                            {campaignData.schedule_config.time_slots.map((slot, index) => (
                              <div key={index} className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                                <div className="flex-1 grid grid-cols-2 gap-3">
                                  {/* Start Time */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Start Time
                                    </label>
                                    <input
                                      type="time"
                                      value={slot.start || '09:00'}
                                      onChange={(e) => {
                                        const newSlots = [...campaignData.schedule_config.time_slots];
                                        newSlots[index] = { ...newSlots[index], start: e.target.value };
                                        setCampaignData({
                                          ...campaignData,
                                          schedule_config: {
                                            ...campaignData.schedule_config,
                                            time_slots: newSlots
                                          }
                                        });
                                      }}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      required
                                    />
                                  </div>

                                  {/* End Time */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      End Time
                                    </label>
                                    <input
                                      type="time"
                                      value={slot.end || '17:00'}
                                      onChange={(e) => {
                                        const newSlots = [...campaignData.schedule_config.time_slots];
                                        newSlots[index] = { ...newSlots[index], end: e.target.value };
                                        setCampaignData({
                                          ...campaignData,
                                          schedule_config: {
                                            ...campaignData.schedule_config,
                                            time_slots: newSlots
                                          }
                                        });
                                      }}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                      required
                                    />
                                  </div>
                                </div>

                                {/* Remove Button */}
                                {campaignData.schedule_config.time_slots.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newSlots = campaignData.schedule_config.time_slots.filter((_, i) => i !== index);
                                      setCampaignData({
                                        ...campaignData,
                                        schedule_config: {
                                          ...campaignData.schedule_config,
                                          time_slots: newSlots
                                        }
                                      });
                                    }}
                                    className="mt-6 px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Remove time slot"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-3 p-2 bg-blue-100 rounded border border-blue-300">
                          <p className="text-xs text-blue-800">
                            💡 <strong>Example:</strong> You can add multiple time slots like 2PM-4PM, 6PM-8PM, and 10PM-11PM. The campaign will automatically start at any start time and stop at the corresponding end time. You can manually pause/resume during these windows.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* NOUVEAUTÉ 2 - Ambiances Sonores */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-4">🎵 Background Sound Ambiance</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select an ambiance
                      </label>
                      <select
                        value={campaignData.background_ambiance}
                        onChange={(e) => setCampaignData({
                          ...campaignData,
                          background_ambiance: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {availableAmbiances.map(ambiance => (
                          <option key={ambiance.id} value={ambiance.id}>
                            {ambiance.name} - {ambiance.description}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        The ambiance will be mixed at 20% of TTS volume to create a realistic environment without masking the voice.
                      </p>
                    </div>

                    {campaignData.background_ambiance !== 'none' && (
                      <div className="bg-green-50 p-3 rounded border border-green-200">
                        <p className="text-xs text-green-800">
                          ✅ <strong>Selected ambiance:</strong> {availableAmbiances.find(a => a.id === campaignData.background_ambiance)?.name}
                          <br />
                          This ambiance will be applied to all calls in this campaign to create a realistic sound environment.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact import */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-3">Contact Import</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CSV File *
                      </label>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleCsvUpload}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    {/* <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={enableDeduplication}
                          onChange={(e) => setEnableDeduplication(e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Enable deduplication</span>
                      </label>
                    </div> */}
                  </div>
                </div>

                {/* CSV preview */}
                {showCsvPreview && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-medium mb-3">Preview and Mapping ({csvData.length} contacts)</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        {Object.keys(csvData[0] || {}).map(header => (
                          <div key={header}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {header}
                            </label>
                            <select
                              value={columnMapping[header] || ''}
                              onChange={(e) => setColumnMapping({ ...columnMapping, [header]: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Ignore</option>
                              <option value="id">ID</option>
                              <option value="name">Name</option>
                              <option value="firstName">First Name</option>
                              <option value="phone">Phone</option>
                              <option value="email">Email</option>
                              <option value="address">Address</option>
                              <option value="city">City</option>
                              <option value="postal">Postal Code</option>
                            </select>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between gap-4 mt-4">
                        <button
                          onClick={processContactsWithoutDuplication}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex-1"
                        >
                          Process Contact (without duplication)
                        </button>
                        <button
                          onClick={processContactsWithDuplication}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex-1"
                        >
                          Process Contact (with duplication)
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Processed contacts */}
                {mappedContacts.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-medium mb-3">Ready Contacts ({mappedContacts.length})</h3>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-800">
                        ✅ {mappedContacts.length} contacts ready to be added to the campaign
                      </p>
                    </div>
                  </div>
                )}

                {/* Lead Qualification */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium mb-4">🎯 Lead Qualification</h3>
                  <div className="bg-gray-50 p-5 rounded-lg space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium">
                          Minimum Positive Answers to Qualify as Lead
                        </label>
                        <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                          {campaignData.lead_qualification?.min_positive_answers || 4}
                          {campaignData.script ? ` / ${campaignData.script.split('##').filter(s => s.trim()).length} questions` : ''}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="20"
                        value={campaignData.lead_qualification?.min_positive_answers || 4}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          lead_qualification: {
                            ...prev.lead_qualification,
                            min_positive_answers: parseInt(e.target.value)
                          }
                        }))}
                        className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>1 (very lenient)</span>
                        <span>20 (very strict)</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        ℹ️ If the user positively and relevantly answers at least this many script questions, they qualify as a lead. FAQ/objection exchanges are not counted.
                      </p>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium">
                          Custom Lead Detection Prompt <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        {campaignData.lead_qualification?.custom_lead_prompt && (
                          <button
                            onClick={() => setCampaignData(prev => ({
                              ...prev,
                              lead_qualification: { ...prev.lead_qualification, custom_lead_prompt: '' }
                            }))}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            ↩️ Reset to Default
                          </button>
                        )}
                      </div>
                      <textarea
                        value={campaignData.lead_qualification?.custom_lead_prompt || ''}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          lead_qualification: {
                            ...prev.lead_qualification,
                            custom_lead_prompt: e.target.value
                          }
                        }))}
                        placeholder={`Default prompt (leave empty to use):\n\nFor each question in the AGENT SCRIPT, check if the user answered it in a POSITIVE and RELEVANT manner.\n\nA POSITIVE/RELEVANT answer means:\n- The user gave genuine, truthful information\n- The user engaged constructively (even if brief)\n- The user agreed or showed willingness\n\nDo NOT count as positive if:\n- The user refused, deflected, or was hostile\n- The user gave nonsense or fake data\n- It was an FAQ/objection side-conversation`}
                        className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        rows={5}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        ℹ️ Leave empty to use the default. Customize only if you need different rules.
                      </p>
                    </div>

                    <CriteriaToggles
                      value={campaignData.lead_qualification}
                      onChange={(patch) => setCampaignData(prev => ({
                        ...prev,
                        lead_qualification: { ...prev.lead_qualification, ...patch }
                      }))}
                    />
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex justify-end space-x-4 pt-4 border-t">
                  <button
                    onClick={() => closeModal(setModalState, 'showCreate')}
                    disabled={creatingCampaign}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createCampaign}
                    disabled={creatingCampaign}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {creatingCampaign && (
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {creatingCampaign ? 'Creating...' : 'Create Campaign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit modal */}
        {modalState.showEdit && editingCampaign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Edit Campaign "{editingCampaign.name}"</h2>
                <button
                  onClick={() => closeModal(setModalState, 'showEdit')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic information */}
                <div>
                  <h3 className="text-lg font-medium mb-3">General Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Name</label>
                      <input
                        type="text"
                        value={campaignData.name}
                        onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: Q1 2024 Prospecting"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <input
                        type="text"
                        value={campaignData.description}
                        onChange={(e) => setCampaignData({ ...campaignData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ex: Commercial prospecting campaign"
                      />
                    </div>
                    {/* Concurrency existed only in the CREATE form, so it could never be
                        changed after launch — the client had to build a new campaign just
                        to adjust it. The backend re-reads this value on every slot refill,
                        so editing it applies to a RUNNING campaign within seconds. */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Simultaneous calls
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="200"
                        value={campaignData.concurrent_calls ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') { setCampaignData({ ...campaignData, concurrent_calls: '' }); return; }
                          const n = Math.max(1, Math.min(200, parseInt(v, 10) || 1));
                          setCampaignData({ ...campaignData, concurrent_calls: n });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Applies immediately, even while the campaign is running.
                        Limited by your telephony provider's channel allowance.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Script */}
                <div>
                  <h3 className="text-lg font-medium mb-3">AI Script</h3>
                  <textarea
                    value={campaignData.script}
                    onChange={(e) => setCampaignData({ ...campaignData, script: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={6}
                    placeholder="Ex: Hello {name}, I'm calling you on behalf of {company}..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use variables like {'{name}'}, {'{firstName}'}, {'{company}'} to personalize your calls
                  </p>
                </div>

                {/* AI Configuration */}
                <div>
                  <h3 className="text-lg font-medium mb-3">AI Configuration</h3>

                  {/* Language Selection - Must be selected first */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Language *
                    </label>
                    <select
                      value={selectedLanguage || campaignData.ai_config?.language || ''}
                      onChange={(e) => {
                        setSelectedLanguage(e.target.value);
                        setCampaignData(prev => ({
                          ...prev,
                          ai_config: {
                            ...(prev.ai_config || DEFAULT_AI_CONFIG),
                            language: e.target.value
                          }
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Language First...</option>
                      <option value="en">English</option>
                      <option value="fr">French</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select language first to filter available models
                    </p>
                  </div>

                  {(selectedLanguage || campaignData.ai_config?.language) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">STT Model</label>
                        <select
                          value={campaignData.ai_config?.stt_model || ''}
                          onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            ai_config: {
                              ...(prev.ai_config || DEFAULT_AI_CONFIG),
                              stt_model: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!selectedLanguage && !campaignData.ai_config?.language}
                        >
                          <option value="">Select Model</option>
                          {getFilteredModels('stt')
                            ?.filter(model => model.status === 'running' || model.status === 'available')
                            .map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name} {model.recommended && '⭐'} {model.tested && '✅'}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">LLM Model</label>
                        <select
                          value={campaignData.ai_config?.llm_model || ''}
                          onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            ai_config: {
                              ...(prev.ai_config || DEFAULT_AI_CONFIG),
                              llm_model: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!selectedLanguage && !campaignData.ai_config?.language}
                        >
                          <option value="">Select Model</option>
                          {getFilteredModels('llm')
                            ?.filter(model => model.status === 'running' || model.status === 'available')
                            .map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name} {model.recommended && '⭐'} {model.tested && '✅'}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">TTS Model</label>
                        <select
                          value={campaignData.ai_config?.tts_model || ''}
                          onChange={(e) => setCampaignData(prev => ({
                            ...prev,
                            ai_config: {
                              ...(prev.ai_config || DEFAULT_AI_CONFIG),
                              tts_model: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!selectedLanguage && !campaignData.ai_config?.language}
                        >
                          <option value="">Select Model</option>
                          {getFilteredModels('tts')
                            ?.filter(model => model.status === 'running' || model.status === 'available')
                            .map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name} {model.recommended && '⭐'} {model.tested && '✅'}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact management */}
                <div>
                  <h3 className="text-lg font-medium mb-3">📞 Contact Management</h3>

                  {/* Contact import */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h4 className="font-medium mb-2">📄 Additional CSV Import</h4>
                    <div className="flex items-center space-x-4">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleAdditionalCSVImport(e.target.files[0]);
                          }
                        }}
                        className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <span className="text-xs text-gray-500">
                        Add contacts from a CSV file (name,firstName,phone,email)
                      </span>
                    </div>
                  </div>

                  {/* Manual contact addition */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-3">➕ Add Manual Contact</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Last name"
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        className="px-3 py-2 border rounded text-sm"
                      />
                      <input
                        type="text"
                        placeholder="First name"
                        value={newContact.firstName}
                        onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                        className="px-3 py-2 border rounded text-sm"
                      />
                      <input
                        type="tel"
                        placeholder="Phone"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        className="px-3 py-2 border rounded text-sm"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        className="px-3 py-2 border rounded text-sm"
                      />
                    </div>
                    <button
                      onClick={addManualContact}
                      disabled={!newContact.phone}
                      className="mt-2 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                    >
                      Add Contact
                    </button>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex justify-end space-x-4 pt-4 border-t">
                  <button
                    onClick={() => closeModal(setModalState, 'showEdit')}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateCampaign}
                    disabled={loading}
                    className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Campaign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enrichment configuration modal */}
        {modalState.showEnrichment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">🧠 Enrichment Configuration - {editingCampaign?.name}</h2>
                <button
                  onClick={() => closeModal(setModalState, 'showEnrichment')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-8">
                {/* Custom fields */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">📝 Custom Enrichment Fields</h3>
                    <button
                      onClick={addCustomField}
                      className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
                    >
                      ➕ Add Field
                    </button>
                  </div>

                  <div className="space-y-4">
                    {customFields.map((field, index) => (
                      <div key={field.id} className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-1">Field Name</label>
                            <input
                              type="text"
                              value={field.name}
                              onChange={(e) => updateCustomField(index, { ...field, name: e.target.value })}
                              placeholder="heating_type"
                              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Label</label>
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => updateCustomField(index, { ...field, label: e.target.value })}
                              placeholder="Heating Type"
                              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Type</label>
                            <select
                              value={field.type}
                              onChange={(e) => updateCustomField(index, { ...field, type: e.target.value })}
                              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="text">Text</option>
                              <option value="number">Number</option>
                              <option value="boolean">Yes/No</option>
                              <option value="select">Multiple Choice</option>
                              <option value="date">Date</option>
                              <option value="time">Time</option>
                            </select>
                          </div>
                          <div className="flex items-end">
                            <button
                              onClick={() => removeCustomField(index)}
                              className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        <div className="mt-3">
                          <label className="block text-sm font-medium mb-1">Description (for LLM)</label>
                          <input
                            type="text"
                            value={field.description}
                            onChange={(e) => updateCustomField(index, { ...field, description: e.target.value })}
                            placeholder="Ex: Extract mentioned heating type (Gas, Oil, Electric, etc.)"
                            className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    ))}

                    {customFields.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No custom fields configured.</p>
                        <p className="text-sm">Click "Add Field" to get started.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Call categories */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">📊 Call Categories</h3>
                    <button
                      onClick={addCallCategory}
                      className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                    >
                      ➕ Add Category
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {callCategories.map((category, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <div className="space-y-3">
                          {category.id && (
                            <div className="text-sm font-semibold text-gray-700">{categoryKey(category.id)}</div>
                          )}
                          <div>
                            <label className="block text-sm font-medium mb-1">Category ID</label>
                            <input
                              type="text"
                              value={category.id || ''}
                              onChange={(e) => updateCallCategory(index, { ...category, id: e.target.value })}
                              placeholder="LEADS_TAKEN"
                              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Name</label>
                            <input
                              type="text"
                              value={category.name || category.label || ''}
                              onChange={(e) => updateCallCategory(index, { ...category, name: e.target.value })}
                              placeholder={categoryKey(category.id) || 'Leads Taken'}
                              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Description</label>
                            <textarea
                              value={category.description || ''}
                              onChange={(e) => updateCallCategory(index, { ...category, description: e.target.value })}
                              placeholder="Interested contact with appointment booking"
                              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                              rows={2}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Lead outcome</label>
                            <select
                              value={category.lead_outcome || defaultLeadOutcome(category.id)}
                              onChange={(e) => updateCallCategory(index, { ...category, lead_outcome: e.target.value })}
                              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                            >
                              {LEAD_OUTCOME_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              "Always a lead" overrides a low positive-answer count; "Never a lead" blocks the export even when the count is high. Eliminatory criteria and answering machines win over both.
                            </p>
                          </div>
                          <div className="flex justify-between items-center">
                            {/* <div>
                              <label className="block text-sm font-medium mb-1">Color</label>
                              <input
                                type="color"
                                value={category.color}
                                onChange={(e) => updateCallCategory(index, { ...category, color: e.target.value })}
                                className="w-16 h-8 border rounded"
                              />
                            </div> */}
                            <button
                              onClick={() => removeCallCategory(index)}
                              className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {callCategories.length === 0 && (
                      <div className="col-span-2 text-center py-8 text-gray-500">
                        <p>No categories configured.</p>
                        <p className="text-sm">Click "Add Category" to get started.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lead Qualification */}
                <div>
                  <h3 className="text-lg font-medium mb-4">🎯 Lead Qualification</h3>
                  <div className="bg-gray-50 p-5 rounded-lg space-y-5">
                    {/* Slider */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium">
                          Minimum Positive Answers to Qualify as Lead
                        </label>
                        <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                          {editingCampaign?.lead_qualification?.min_positive_answers || 4}
                          {editingCampaign?.script ? ` / ${editingCampaign.script.split('##').filter(s => s.trim()).length} questions` : ''}
                        </span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="20"
                        value={editingCampaign?.lead_qualification?.min_positive_answers || 4}
                        onChange={(e) => setEditingCampaign(prev => ({
                          ...prev,
                          lead_qualification: {
                            ...prev.lead_qualification,
                            min_positive_answers: parseInt(e.target.value)
                          }
                        }))}
                        className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>1 (very lenient)</span>
                        <span>20 (very strict)</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        ℹ️ If the user positively and relevantly answers at least this many script questions, they qualify as a lead. FAQ/objection exchanges are not counted.
                      </p>
                    </div>

                    {/* Custom Prompt */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium">
                          Custom Lead Detection Prompt <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        {editingCampaign?.lead_qualification?.custom_lead_prompt && (
                          <button
                            onClick={() => setEditingCampaign(prev => ({
                              ...prev,
                              lead_qualification: {
                                ...prev.lead_qualification,
                                custom_lead_prompt: ''
                              }
                            }))}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            ↩️ Reset to Default
                          </button>
                        )}
                      </div>
                      <textarea
                        value={editingCampaign?.lead_qualification?.custom_lead_prompt || ''}
                        onChange={(e) => setEditingCampaign(prev => ({
                          ...prev,
                          lead_qualification: {
                            ...prev.lead_qualification,
                            custom_lead_prompt: e.target.value
                          }
                        }))}
                        placeholder={`Default prompt (leave empty to use):\n\nFor each question in the AGENT SCRIPT, check if the user answered it in a POSITIVE and RELEVANT manner.\n\nA POSITIVE/RELEVANT answer means:\n- The user gave genuine, truthful information related to the question\n- The user engaged constructively (even if brief)\n- The user agreed or showed willingness\n\nDo NOT count as positive if:\n- The user refused to answer or deflected\n- The user was hostile, sarcastic, or mocking\n- The user gave nonsense or clearly fake data\n- The user ignored the question entirely`}
                        className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        rows={6}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        ℹ️ Leave empty to use the default lead detection prompt. Customize only if you need different qualification rules.
                      </p>
                    </div>

                    <CriteriaToggles
                      value={editingCampaign?.lead_qualification}
                      onChange={(patch) => setEditingCampaign(prev => ({
                        ...prev,
                        lead_qualification: { ...prev.lead_qualification, ...patch }
                      }))}
                    />
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex justify-end space-x-4 pt-4 border-t">
                  <button
                    onClick={() => closeModal(setModalState, 'showEnrichment')}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEnrichmentConfig}
                    disabled={savingEnrichment}
                    className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    {savingEnrichment ? 'Saving...' : '💾 Save Configuration'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Details modal with complete component */}
        {modalState.showDetail && selectedCampaign && (
          <CampaignDetails
            campaign={selectedCampaign}
            onClose={() => closeModal(setModalState, 'showDetail')}
          />
        )}

        {/* Export Format Selection Modal */}
        {modalState.showExportFormat && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Choose Export Format</h2>
                <button
                  onClick={() => {
                    closeModal(setModalState, 'showExportFormat');
                    setExportingCampaignId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Select the format you want to export your campaign data:
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => handleExportFormat('xlsx')}
                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2"
                  >
                    <span>📊</span>
                    <span>Export as XLSX</span>
                  </button>
                  <button
                    onClick={() => handleExportFormat('csv')}
                    className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center space-x-2"
                  >
                    <span>📄</span>
                    <span>Export as CSV</span>
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={() => {
                    closeModal(setModalState, 'showExportFormat');
                    setExportingCampaignId(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {modalState.showDeleteConfirm && campaignToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Confirm Deletion
                </h3>
                <p className="text-gray-700 mb-6">
                  Are you sure you want to delete the campaign <span className="font-semibold">"{campaignToDelete.name}"</span>?
                  This action is irreversible.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      closeModal(setModalState, 'showDeleteConfirm');
                      setCampaignToDelete(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteCampaign}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ♻️ Recycle-contacts modal (client feature #5) */}
      {recycleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">♻️ Recycle contacts</h2>
              <button onClick={() => setRecycleModal(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Create a follow-up campaign from the contacts of «{recycleModal.campaign.name}» whose last
              call ended in the selected categories. The new campaign reuses the same script and voice
              (no audio to regenerate) and starts paused.
            </p>

            {recycleModal.options.categories.some(c => c.count > 0) && (
              <div className="mb-4">
                <div className="text-sm font-semibold mb-2">Call categories (AI analysis)</div>
                {recycleModal.options.categories.filter(c => c.count > 0).map(c => (
                  <label key={c.id} className="flex items-center gap-2 py-1 cursor-pointer">
                    <input type="checkbox" checked={recycleModal.cats.includes(c.id)}
                      onChange={() => setRecycleModal(prev => ({ ...prev,
                        cats: prev.cats.includes(c.id) ? prev.cats.filter(x => x !== c.id) : [...prev.cats, c.id] }))} />
                    <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: c.color || '#94a3b8' }}></span>
                    <span className="text-sm">{c.name}</span>
                    <span className="text-xs text-gray-500 ml-auto">{c.count} contacts</span>
                  </label>
                ))}
              </div>
            )}

            <div className="mb-4">
              <div className="text-sm font-semibold mb-2">Call outcomes</div>
              {recycleModal.options.outcomes.filter(o => o.count > 0).map(o => (
                <label key={o.id} className="flex items-center gap-2 py-1 cursor-pointer">
                  <input type="checkbox" checked={recycleModal.outs.includes(o.id)}
                    onChange={() => setRecycleModal(prev => ({ ...prev,
                      outs: prev.outs.includes(o.id) ? prev.outs.filter(x => x !== o.id) : [...prev.outs, o.id] }))} />
                  <span className="text-sm">{o.label}</span>
                  <span className="text-xs text-gray-500 ml-auto">{o.count} contacts</span>
                </label>
              ))}
              {recycleModal.options.outcomes.every(o => !o.count) &&
                recycleModal.options.categories.every(c => !c.count) && (
                <p className="text-sm text-gray-500">No calls analysed yet for this campaign.</p>
              )}
            </div>

            <div className="mb-4">
              <div className="text-sm font-semibold mb-2">What should happen?</div>
              <label className="flex items-start gap-2 py-1 cursor-pointer">
                <input type="radio" name="recycle-mode" className="mt-1"
                  checked={recycleModal.mode === 'same'}
                  onChange={() => setRecycleModal(prev => ({ ...prev, mode: 'same' }))} />
                <span className="text-sm">
                  <b>Recycle in this campaign</b>
                  <span className="block text-xs text-gray-500">
                    The selected contacts go back to «à appeler» here. Same schedule, same statistics.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 py-1 cursor-pointer">
                <input type="radio" name="recycle-mode" className="mt-1"
                  checked={recycleModal.mode === 'new'}
                  onChange={() => setRecycleModal(prev => ({ ...prev, mode: 'new' }))} />
                <span className="text-sm">
                  <b>Create a separate campaign</b>
                  <span className="block text-xs text-gray-500">
                    Keeps this campaign untouched. The new one starts paused.
                  </span>
                </span>
              </label>
            </div>

            {recycleModal.mode === 'new' && (
              <div className="mb-4">
                <label className="text-sm font-semibold block mb-1">New campaign name</label>
                <input type="text" value={recycleModal.name}
                  onChange={e => setRecycleModal(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button onClick={() => setRecycleModal(null)}
                className="px-4 py-2 rounded-md text-sm border border-gray-300 hover:bg-gray-50">Cancel</button>
              <button onClick={submitRecycle} disabled={recycleModal.creating}
                className={`px-4 py-2 rounded-md text-sm text-white ${recycleModal.creating ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'}`}>
                {recycleModal.creating ? '⏳ Working…'
                  : recycleModal.mode === 'same' ? '♻️ Recycle these contacts' : '♻️ Create follow-up campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimplifiedCampaigns;